/*
 * Nosto Search JS
 * Handles Nosto-powered search and category pages, including facets,
 * pagination, and product rendering.
 */

class NostoSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.preloadedImageUrls = new Set();
    this.searchTerm = '';
    this.searchableFacets = ['brand'];
    this.openedFacets = [];
    this.activeMaxPrice = null;
    this.lastEditedFacet = null;
    this.includeFacetRules = { 'beauty': 'sub-category' };
    this.excludeFacets = [];
    this.usingFallback = false;
    this.fallbackAttemptTime = null;
    this.retryCount = 0;
    this.maxRetries = 3;
    this.initialLoadComplete = false;
  }

  connectedCallback() {
    this.options = JSON.parse(this.getAttribute('data-options') || '{}');
    this.maxProducts = this.options.maxProducts ? parseInt(this.options.maxProducts) : null;
    this.loadingSpinner = this.querySelector('.loading-overlay');
    this.productGrid = this.querySelector('[data-product-grid-nosto]');
    this.container = this;

    this.gridySlider = this.querySelector('gridy-slider');

    this.iprDesktop = this.options.iprDesktop ?
      parseInt(this.options.iprDesktop) :
      (this.gridySlider ? parseInt(this.gridySlider.dataset.iprDesktop) : null);

    this.iprTablet = this.options.iprTablet ?
      parseInt(this.options.iprTablet) :
      (this.gridySlider ? parseInt(this.gridySlider.dataset.iprTablet) : null);

    this.iprMobile = this.options.iprMobile ?
      parseInt(this.options.iprMobile) :
      (this.gridySlider ? parseInt(this.gridySlider.dataset.iprMobile) : null);

    this.arrows = this.querySelector('.gridy-arrows');

    this.searchForm = this.querySelector('form[role="search"]');
    this.input = this.querySelector('input[type="search"]');
    this.resultsCount = this.querySelector('#products-count-ajax');
    this.header = this.querySelector('header h1');
    this.facetsFormContainer = document.querySelector('.filter-form');
    this.abortController = new AbortController();
    this.sortBy = this.querySelector('#sort-by');
    this.viewMoreButton = this.querySelector('#pagination-ajax button');
    this.showCount = this.querySelector('[data-show-count]');
    this.resultsSize = parseInt(this.productGrid?.getAttribute('data-results-per-page')) || 24;
    this.context = this.productGrid?.getAttribute('data-context');
    this.collectionId = this.productGrid?.getAttribute('data-collection-id') || null;
    this.collectionTitle = this.productGrid?.getAttribute('data-collection') || null;

    if (this.context === 'serp') {
      this.excludeFacets.push('sub-category');
    }

    this.enableFallback = JSON.parse(this.options.enableFallback || 'false');

    if (!this.productGrid) {
      console.error('Nosto: Required product grid element not found');
      return;
    }

    this.setLoadingState();
    this.setupClickTracking();
    this.setupEventListeners();
    this.initializeFromURL();
  }

  /**
   * Generate HTML markup for products returned from Nosto API
   * @param {Array} products - Array of product data from Nosto
   * @returns {Promise<string>} HTML markup for the product grid
   */
  async generateProductsMarkup(products) {
    if (!products || !Array.isArray(products)) {
      console.warn('Invalid products data provided to generateProductsMarkup');
      return '';
    }

    // Get template content
    const template = document.querySelector('.js-card-product-template');
    if (!template) {
      console.warn('Product card template not found');
      return '';
    }

    const productsMarkup = await Promise.all(products.map(async (product, index) => {
      if (!product) return '';

      const templateContent = template.content.cloneNode(true);
      const container = document.createElement('div');
      container.appendChild(templateContent);

      try {
        // Extract product data with fallbacks
        const productHandle = product.url?.split('/').slice(-1)[0] || '';
        const productUrlRelative = productHandle ? `/products/${productHandle}` : '#';
        const productPrice = product.price ? product.price : '';
        const productPriceWithFormatting = productPrice ? Woolman.Utils.formatMoney(productPrice * 100) : '';
        const productListPrice = product.listPrice || product.list_price || '';
        const productListPriceWithFormatting = productListPrice ? Woolman.Utils.formatMoney(productListPrice * 100) : '';
        const onDiscount = productListPrice && productPrice < productListPrice;
        const productThumbUrl = product.imageUrl || product.image_url || '';
        const productAltThumbUrls = product.alternateImageUrls || product.alternate_image_urls || '';
        const productName = product.name || product.product_name || 'Product';
        const productBrand = product.brand || product.vendor || '';
        const productId = product.productId || product.product_id || '';
        const variantId = product.skus[0]?.id || null;
        const variantIds = product.skus ? product.skus.map(sku => sku.id) : [];
        const customFields = product.customFields || product.custom_fields || {};

        // Replace placeholders
        container.innerHTML = container.innerHTML
          .replace(/\[\[product\.url\]\]/g, productUrlRelative)
          .replace(/\[\[product\.id\]\]/g, productId)
          .replace(/\[\[product\.variant_id\]\]/g, variantId || '')
          .replace(/\[\[product\.handle\]\]/g, productHandle);

        const cardType = this.options.cardType;
        const cardTextAlign = this.options.cardTextAlign;

        const cardElement = container.querySelector('.card--product');
        cardElement.setAttribute('data-index', index + 1);
        if (cardType) {
          cardElement.classList.add(`card--${cardType}`);
        }
        if (cardTextAlign) {
          cardElement.querySelector('.card-body').classList.add(cardTextAlign);
        }
        const productDataSpan = document.createElement('span');
        productDataSpan.className = 'hidden';
        productDataSpan.setAttribute('data-product-json', productId);
        productDataSpan.innerHTML = JSON.stringify(product);
        cardElement.appendChild(productDataSpan);

        const productAriaLabel = window.accessibilityStrings.viewSomething.replace('{{ something }}', productName);
        container.querySelector('.card-media').setAttribute('aria-label', productAriaLabel);
        container.querySelector('.card-title a').setAttribute('aria-label', productAriaLabel);

        // Set primary image for normal product cards
        this._setProductImage(container, productThumbUrl, productName, index, productAltThumbUrls);

        // Set badges
        this._setProductBadges(container, customFields, product);

        // Set product title
        const titleElement = container.querySelector('[data-product-title]');
        if (titleElement) {
          titleElement.textContent = productName;
        }

        // Set vendor/brand
        const vendorElement = container.querySelector('[data-vendor]');
        if (vendorElement && productBrand) {
          vendorElement.textContent = productBrand;
        }

        // Set price
        this._setProductPrice(container, productPriceWithFormatting, productListPriceWithFormatting, onDiscount, productBrand, product);

        // Switch CTA behavior based on variant count.
        // Nosto only returns available SKUs, so a multi-size product with 1 size
        // in stock appears as skus.length === 1. To distinguish:
        // - Shopify variant names with multiple options use " / " separator
        //   (e.g. "Black / 26" = colour + size). These MUST show Quick Shop so
        //   the user sees which size they'd get.
        // - Single-option variants (e.g. "Black") or "Default Title" have no " / ".
        //   The user has no choice to make — show Add to Bag.
        const firstSkuName = (product.skus?.[0]?.name) || '';
        const hasMultipleOptionDimensions = firstSkuName.includes(' / ');
        // Whether the product would be a single-choice "Add to Bag" ignoring siblings.
        const baseSingleVariant = variantIds.length === 1 && !hasMultipleOptionDimensions;
        // Sibling-linked colour products are separate products (one SKU, no " / " in
        // the name), so the " / " heuristic alone misclassifies them as Add to Bag.
        // If sibling linking is present, optimistically show Quick Shop so the customer
        // can pick a sibling colour. The deferred swatch fetch reverts this to Add to
        // Bag if the sibling link resolves to just this product (nothing to choose).
        const hasSiblingLinking = Boolean(this._getSiblingLinkingGid(customFields));
        const hasSingleVariant = baseSingleVariant && !hasSiblingLinking;
        cardElement.setAttribute('data-has-single-variant', hasSingleVariant ? 'true' : 'false');
        // Preserve the sibling-independent decision so the deferred fetch can revert
        // an optimistic Quick Shop back to Add to Bag when there is no real choice.
        cardElement.setAttribute('data-single-variant-base', baseSingleVariant ? 'true' : 'false');
        const isFashionContext = this._isFashionCollection();

        const quickBuyElement = container.querySelector('quick-buy');
        if (quickBuyElement) {
          quickBuyElement.dataset.fashionContext = isFashionContext ? 'true' : 'false';
        }

        const ctaTextEls = container.querySelectorAll('[data-cta-text]');
        ctaTextEls.forEach((el) => {
          el.textContent = hasSingleVariant ? 'ADD TO BAG' : 'QUICK SHOP';
        });

        // Touch devices have no hover state: place CTA below reviews (or price) for persistent visibility.
        this._relocateCardCtaForTouchDevices(container);

        if (hasSingleVariant) {
          const directAddVariantInput = container.querySelector('product-form input[name="id"]');
          if (directAddVariantInput) {
            directAddVariantInput.value = String(variantId);
            directAddVariantInput.disabled = false;
          }
        } else {
          // Defensive: disable the direct-add form when variant data is not suitable.
          const directAddButton = container.querySelector('product-form button[name="add"]');
          if (directAddButton) {
            directAddButton.setAttribute('disabled', 'disabled');
            directAddButton.setAttribute('aria-disabled', 'true');
          }
        }

        // Store product data for deferred swatch loading
        // This prevents blocking the initial render while still allowing swatches to load later
        const swatchesContainer = container.querySelector('[data-product-swatches]');
        if (swatchesContainer) {
          swatchesContainer.setAttribute('data-product-handle', productHandle);
          swatchesContainer.setAttribute('data-custom-fields', JSON.stringify(customFields));
        }

        // Add fade-in class with staggered delay
        if (cardElement) {
          // Add a small delay based on index for staggered animation
          cardElement.style.animationDelay = `${index * 0.05}s`;
          cardElement.classList.add('fade-in');
          cardElement.setAttribute("data-track-click", "product_card_click");
          cardElement.setAttribute("data-track-product-id", productId);
        }

        return container.innerHTML;
      } catch (error) {
        console.error('Error processing product markup:', error);
        return '';
      }
    }));

    // Handle arrows visibility based on product count
    this._handleArrowsVisibility(products);

    return productsMarkup.join('');
  }

  /**
   * Load swatches for all product cards after initial render
   * This method is called after the product grid is updated with new cards
   * Uses Intersection Observer to load swatches only when cards are visible
   */
  async loadDeferredSwatches() {
    const swatchContainers = this.productGrid.querySelectorAll('[data-product-swatches][data-product-handle]');

    if (!swatchContainers.length) return;

    // Use Intersection Observer to load swatches only when they're near the viewport
    const observerOptions = {
      root: null,
      rootMargin: '200px', // Start loading 200px before entering viewport
      threshold: 0
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const container = entry.target;
          this._loadSingleCardSwatches(container);
          observer.unobserve(container); // Stop observing once loaded
        }
      });
    }, observerOptions);

    // Observe all swatch containers
    swatchContainers.forEach(container => observer.observe(container));
  }

  /**
   * Load swatches for a single product card
   * @param {HTMLElement} swatchContainer - The swatch container element
   */
  async _loadSingleCardSwatches(swatchContainer) {
    try {
      const productHandle = swatchContainer.getAttribute('data-product-handle');
      const customFieldsStr = swatchContainer.getAttribute('data-custom-fields');

      if (!productHandle) return;

      const customFields = customFieldsStr ? JSON.parse(customFieldsStr) : {};

      // Get the product card container for hover functionality
      const productCard = swatchContainer.closest('.card--product');
      const productData = productCard?.querySelector('[data-product-json]');
      const product = productData ? JSON.parse(productData.textContent) : null;

      if (!product || !productCard) return;

      // Call the existing swatch generation method, passing the product card as container
      await this._setProductSwatches(productCard, product, customFields, swatchContainer);

      // Clean up data attributes after loading
      swatchContainer.removeAttribute('data-product-handle');
      swatchContainer.removeAttribute('data-custom-fields');
    } catch (error) {
      console.error('Error loading deferred swatches:', error);
    }
  }

  /**
   * Helper method to set product image
   */
  _setProductImage(container, thumbUrl, productName, index, alternateImageUrls) {
    const primaryImageContainer = container.querySelector('.card-media');
    if (!primaryImageContainer || !thumbUrl) return;

    const cardImgAspectRatio = this.options.cardImgAspectRatio;
    const cardImgSizingRule = this.options.cardImgSizingRule;

    // Determine loading strategy based on viewport position
    let loadingStrategy = 'lazy';
    let fetchPriority = 'low';
    if (!this.gridySlider) {
      let visibleItemsCount;
      if (window.matchMedia && window.matchMedia('(max-width: 767px)').matches) {
        visibleItemsCount = this.iprMobile || 2;
      } else {
        visibleItemsCount = this.iprDesktop || 4;
      }
      loadingStrategy = index < visibleItemsCount ? 'eager' : 'lazy';
      fetchPriority = index < visibleItemsCount ? 'high' : 'low';
    }

    // Render the primary image
    const figureElement = document.createElement('figure');
    figureElement.className = `media ${cardImgSizingRule} ${cardImgAspectRatio}`;
    const srcset = [
      `${thumbUrl}&width=90 90w`,
      `${thumbUrl}&width=180 180w`,
      `${thumbUrl}&width=280 280w`,
      `${thumbUrl}&width=390 390w`,
      `${thumbUrl}&width=540 540w`,
      `${thumbUrl}&width=768 768w`,
      `${thumbUrl}&width=1080 1080w`,
      `${thumbUrl}&width=1280 1280w`,
      `${thumbUrl}&width=1500 1500w`
    ].join(', ');
    figureElement.innerHTML = `
      <img
        srcset="${srcset}"
        sizes="100vw"
        src="${thumbUrl}"
        alt="${productName}"
        loading="${loadingStrategy}"
        fetchpriority="${fetchPriority}"
        decoding="async"
        onload="this.classList.add('loaded')"
      >
    `;
    primaryImageContainer.insertBefore(figureElement, primaryImageContainer.firstChild);

    // Build the full image URL list for the carousel.
    // Fashion collections (configured per section) show all images; all others cap at 2.
    const allImages = [thumbUrl];
    if (alternateImageUrls && alternateImageUrls.length > 0) {
      const maxImages = this._isFashionCollection() ? (alternateImageUrls.length + 1) : 2;
      allImages.push(...alternateImageUrls.slice(0, maxImages - 1));
    }

    // Store image data on the card element so carousel setup can run after DOM insertion
    const cardElement = container.querySelector('.card--product');
    if (cardElement) {
      cardElement.dataset.imgUrls = JSON.stringify(allImages);
      cardElement.dataset.imgCount = allImages.length;
      cardElement.dataset.imgIndex = '0';
      if (allImages.length > 1) {
        cardElement.classList.add('nosto-carousel');
      }
    }
  }

  /**
   * Helper method to set product badges
   */
  _setProductBadges(container, customFields, product = {}) {
    try {
      const badgesElement = container.querySelector('[data-product-badges]');
      if (!badgesElement) return;

      let badgeMarkup = '';
      let promotionData = null;

      if (window.offerPromotionMap && Array.isArray(product.categoryIds)) {
        for (const categoryId of product.categoryIds) {
          const hit = window.offerPromotionMap[categoryId] || window.offerPromotionMap[String(categoryId)];
          if (hit) {
            promotionData = hit;
            break;
          }
        }
      }
      if (!promotionData && window.currentOfferPromotion) {
        promotionData = window.currentOfferPromotion;
      }

      if (typeof promotionData === 'string') {
        try {
          promotionData = JSON.parse(promotionData);
        } catch (e) {
          promotionData = null;
        }
      }

      const tagSvg = '<svg role="presentation" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 25 25" fill="none"><path d="M9.9157 23.6893L1.18957 14.9632C0.60378 14.3774 0.603779 13.4277 1.18957 12.8419L12.8421 1.18934C13.1234 0.908035 13.5049 0.75 13.9028 0.75H20.917C21.2416 0.75 21.5574 0.855267 21.817 1.05L23.5289 2.3339C23.9066 2.61718 24.1289 3.06176 24.1289 3.5339V10.9761C24.1289 11.374 23.9709 11.7555 23.6896 12.0368L12.037 23.6893C11.4512 24.2751 10.5015 24.2751 9.9157 23.6893Z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/><circle cx="16.9763" cy="7.76693" r="2.0339" stroke="currentColor" stroke-width="1.5"/></svg>';
      const giftSvg = window.giftIconSvg || '<svg role="presentation" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 12 20 22 4 22 4 12"></polyline><rect x="2" y="7" width="20" height="5"></rect><line x1="12" y1="22" x2="12" y2="7"></line><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"></path><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"></path></svg>';
      const percentageSvg = window.percentageIconSvg || '';

      // Gift icon markup is tracked separately so it can be prepended to the
      // final badge output — the gift badge must always appear first when
      // multiple badges render on a card.
      let giftBadgeMarkup = '';
      // Track which promotion type produced an icon badge, so the matching
      // custom text badge (e.g. "Spend & Save") can be suppressed.
      let promoTypeRendered = null;

      if (promotionData && typeof promotionData === 'object') {
        const { promotion_type, icon_type, headline, background_color, text_color } = promotionData;

        let iconSvg = '';
        if (icon_type === 'tag') iconSvg = tagSvg;
        else if (icon_type === 'gift') iconSvg = giftSvg;
        else if (icon_type === 'percentage') iconSvg = percentageSvg;

        if (iconSvg) {
          const promoClass = `promo-icon-badge--${promotion_type}`;
          const styleParts = [];
          if (background_color) styleParts.push(`background-color: ${background_color}`);
          if (text_color) styleParts.push(`color: ${text_color}`);
          const styleAttr = styleParts.length ? ` style="${styleParts.join('; ')}"` : '';

          const promoBadgeHtml = `
            <div class="promo-icon-badge ${promoClass}"${styleAttr} aria-label="${headline || ''}">
              <span class="promo-icon-badge__icon">${iconSvg}</span>
            </div>
          `;
          if (icon_type === 'gift') {
            giftBadgeMarkup = promoBadgeHtml;
          } else {
            badgeMarkup += promoBadgeHtml;
          }
          promoTypeRendered = promotion_type;
        }
      }

      // Then add existing custom field badges
      let customBadges;
      if (Array.isArray(customFields)) {
        customBadges = customFields.find(f => f.key === 'woolman-custom_badges')?.value;
      } else if (typeof customFields === 'object' && customFields !== null) {
        customBadges = customFields['woolman-custom_badges'];
      }

      if (customBadges && customBadges.length > 0) {
        customBadges = typeof customBadges === 'string' ? JSON.parse(customBadges) : customBadges;
        if (window.badges) {
          // Separate icon badges from text badges
          const iconBadges = [];
          const textBadges = [];

          customBadges.forEach(badgeName => {
            const badge = window.badges[badgeName];
            if (!badge) return;
            // Normalize name so "Spend & Save" / "Complimentary Gift" / "spend_save"
            // all reduce to the kebab-case promotion_type identifier.
            const normalizedName = badgeName
              .toLowerCase()
              .replace(/&/g, ' ')
              .replace(/[\s_]+/g, '-');
            // Suppress the custom text badge when the matching promo icon badge
            // has already been emitted for this promotion type (e.g. spend-save).
            if (promoTypeRendered && normalizedName === promoTypeRendered) return;
            // The Complimentary Gift badge is rendered as the gift promo-icon-badge
            // to match the offer-collection gift-with-purchase treatment. Suppress it
            // if a gift promo badge has already been emitted for this product.
            if (normalizedName === 'complimentary-gift') {
              if (giftBadgeMarkup) return;
              giftBadgeMarkup = `
                <div class="promo-icon-badge promo-icon-badge--gift-with-purchase" aria-label="${badge.label || ''}">
                  <span class="promo-icon-badge__icon">${giftSvg}</span>
                </div>
              `;
              return;
            }
            if (badge.icon_type) {
              iconBadges.push({ name: badgeName, badge });
            } else {
              textBadges.push({ name: badgeName, badge });
            }
          });

          // Icon badges (after promotion badge), then text badges
          const orderedBadges = [...iconBadges, ...textBadges];

          const customBadgeMarkup = orderedBadges.map(({ name, badge }) => {
            if (badge.icon_type) {
              return `
                <span
                  class="badge badge--icon badge--${name.toLowerCase()}"
                  style="
                    --text-color: ${badge.text_color};
                    --background-color: ${badge.background_color};
                  "
                  aria-label="${String(badge.label || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\"/g, '&quot;')}"
                >
                  ${badge.icon_svg || ''}
                </span>
              `;
            }
            return `
              <span
                class="badge ${name.toLowerCase()}"
                style="
                  --text-color: ${badge.text_color};
                  --background-color: ${badge.background_color};
                "
              >
                ${badge.label}
              </span>
            `;
          }).join('');

          badgeMarkup += customBadgeMarkup;
        }
      }

      if (giftBadgeMarkup || badgeMarkup) {
        badgesElement.innerHTML = giftBadgeMarkup + badgeMarkup;
      }
    } catch (error) {
      console.error('Error setting product badges:', error);
    }
  }

  /**
   * Helper method to set product price
   */
  _setProductPrice(container, price, listPrice, onDiscount, productBrand, product) {
    const priceElement = container.querySelector('[data-price]');
    let salePriceHtml = '';
    let shouldUseBlackPricing = false;

    // Check if this vendor should use black pen pricing
    if (productBrand && window.blackPenVendors?.length > 0 && window.blackPenVendors.find((item) => item.vendor.toLowerCase() == productBrand.toLowerCase())) {
      shouldUseBlackPricing = true;
    }

    // Add black pen pricing class to the card if applicable
    const cardElement = container.querySelector('.card--product');
    if (cardElement && shouldUseBlackPricing && onDiscount) {
      cardElement.classList.add('black-pen-pricing');
    }

    // Check if any SKUs/variants are on sale or have lower/higher prices
    let hasVariantOnSale = false;
    let minSkuPrice = null;
    let maxSkuPrice = null;
    let showPriceRange = false;

    if (product && product.skus && Array.isArray(product.skus) && product.skus.length > 1) {
      for (const sku of product.skus) {
        const skuPrice = sku.price;
        const skuListPrice = sku.listPrice;

        // Track the minimum and maximum SKU prices
        if (skuPrice && typeof skuPrice === 'number') {
          if (minSkuPrice === null || skuPrice < minSkuPrice) {
            minSkuPrice = skuPrice;
          }
          if (maxSkuPrice === null || skuPrice > maxSkuPrice) {
            maxSkuPrice = skuPrice;
          }
        }

        // Check if this SKU is on sale (has both prices and sale price is lower)
        if (skuListPrice && skuPrice && typeof skuPrice === 'number' && typeof skuListPrice === 'number' && skuPrice < skuListPrice) {
          hasVariantOnSale = true;
        }
      }

      // Show price range if we have multiple variants with different prices
      if (minSkuPrice !== null && maxSkuPrice !== null && minSkuPrice !== maxSkuPrice) {
        showPriceRange = true;

        // If any variant is on sale, mark the entire range as on sale
        if (hasVariantOnSale) {
          onDiscount = true;
        }
      }
    }

    // Set up price display
    let priceDisplay;
    if (showPriceRange) {
      const minPriceFormatted = Woolman.Utils.formatMoney(minSkuPrice * 100);
      const maxPriceFormatted = Woolman.Utils.formatMoney(maxSkuPrice * 100);
      priceDisplay = `${minPriceFormatted} - ${maxPriceFormatted}`;
    } else {
      priceDisplay = price;
    }

    if (onDiscount) {
      priceElement.classList.add('price--on-sale');
      if (shouldUseBlackPricing) {
        priceElement.classList.add('price--black-pen');
      }
      salePriceHtml = `
    <span class="visually-hidden">Sale price</span>
    <span class="price-item price-item--sale price-item--last">${priceDisplay}</span>
    `;
    }

    if (priceElement) {
      priceElement.innerHTML = `
          <div class="flex">
            <div class="price__regular">
              <span class="visually-hidden">Regular price</span>
              <span class="price-item price-item--regular">${priceDisplay}</span>
            </div>
            ${listPrice && !showPriceRange ? `
              <div class="price__sale">
                <span class="visually-hidden">Regular price</span>
                <span>
                  <s class="price-item price-item--regular style--subdued">${listPrice}</s>
                  ${salePriceHtml}
                </span>

              </div>
            ` : showPriceRange && onDiscount ? `
              <div class="price__sale">
                <span class="visually-hidden">Sale price</span>
                <span class="price-item--sale">
                  ${salePriceHtml}
                </span>
              </div>
            ` : ''}
          </div>
        `;
    }
  }

  _generateContentSlotElement(contentSlot) {
    const contentSlotUrl = contentSlot.link;
    const contentSlotImageAlt = contentSlot.imageAltText || '';
    const contentSlotImageUrl = contentSlot.image || '';
    const contentSlotPosition = contentSlot.position;
    const contentSlotKey = contentSlot.key || `${contentSlotPosition || ''}|${contentSlotUrl || ''}|${contentSlotImageUrl || ''}`;

    const contentCardTemplate = document.querySelector('.js-card-content-template');
    const contentCardTemplateContent = contentCardTemplate.content.cloneNode(true);
    const contentCardContainer = document.createElement('div');
    contentCardContainer.appendChild(contentCardTemplateContent);
    if (contentSlotUrl)
      contentCardContainer.innerHTML = contentCardContainer.innerHTML
        .replace(/\[\[product\.url\]\]/g, contentSlotUrl)
    const contentAriaLabel = window.accessibilityStrings.viewSomething.replace('{{ something }}', contentSlotImageAlt);
    contentCardContainer.querySelector('.card-media').setAttribute('aria-label', contentAriaLabel);
    this._setProductImage(contentCardContainer, contentSlotImageUrl, contentSlotImageAlt, contentSlotPosition, null);
    const slotElement = contentCardContainer.querySelector('.card--content-slot');
    if (slotElement) {
      slotElement.setAttribute('data-content-slot-key', contentSlotKey);
    }
    return slotElement;
  }

  /**
   * Helper method to handle slider arrows visibility
   */
  _handleArrowsVisibility(products) {
    try {
      if (this.gridySlider && this.iprDesktop && this.arrows) {
        if (products.length <= this.iprDesktop) {
          this.arrows.style.display = 'none';
        } else {
          // Ensure arrows are shown if there are more products than iprDesktop
          this.arrows.style.display = '';
        }
      }
    } catch (error) {
      console.error('Error handling arrows visibility:', error);
    }
  }

  /**
   * Helper method to set product swatches from sibling products or variants
   * @param {HTMLElement} container - The container element (can be temp container or actual card)
   * @param {Object} product - The product data
   * @param {Object} customFields - Custom fields from the product
   * @param {HTMLElement} targetSwatchContainer - Optional specific swatch container to update
   */
  /**
   * Extract the sibling-linking metaobject GID from a product's Nosto custom fields.
   * Nosto may expose custom fields as an array of {key, value} pairs or as a plain object.
   * @param {Array|Object} customFields
   * @returns {string|null} The metaobject GID, or null when not present.
   */
  _getSiblingLinkingGid(customFields) {
    if (!customFields) return null;
    let gid = null;
    if (Array.isArray(customFields)) {
      const siblingField = customFields.find(f => f.key === 'woolman-sibling_linking');
      gid = siblingField?.value;
    } else if (typeof customFields === 'object') {
      gid = customFields['woolman-sibling_linking'];
    }
    return gid || null;
  }

  /**
   * Force a card into Quick Shop mode (used when siblings are discovered after render).
   * @param {HTMLElement} container - The card element or a wrapper containing it.
   */
  _ensureQuickShopCta(container) {
    if (!container) return;
    const card = container.matches?.('.card--product')
      ? container
      : container.querySelector('.card--product');
    if (!card || card.getAttribute('data-has-single-variant') !== 'true') return;

    card.setAttribute('data-has-single-variant', 'false');
    card.querySelectorAll('[data-cta-text]').forEach((el) => {
      el.textContent = 'QUICK SHOP';
    });
  }

  /**
   * Revert an optimistic Quick Shop card back to Add to Bag when a sibling link
   * turns out to resolve to just this product (no colour/option choice to make).
   * Only affects cards that would be single-variant without the sibling link.
   * @param {HTMLElement} container - The card element or a wrapper containing it.
   * @param {Object} product - The product data (for the direct-add variant id).
   */
  _revertToAddToBagIfBase(container, product) {
    if (!container) return;
    const card = container.matches?.('.card--product')
      ? container
      : container.querySelector('.card--product');
    if (!card) return;
    // Products with a genuine size/option choice must stay Quick Shop.
    if (card.getAttribute('data-single-variant-base') !== 'true') return;
    if (card.getAttribute('data-has-single-variant') === 'true') return;

    card.setAttribute('data-has-single-variant', 'true');
    card.querySelectorAll('[data-cta-text]').forEach((el) => {
      el.textContent = 'ADD TO BAG';
    });

    const variantId = product?.skus?.[0]?.id;
    const directAddVariantInput = card.querySelector('product-form input[name="id"]');
    if (directAddVariantInput && variantId) {
      directAddVariantInput.value = String(variantId);
      directAddVariantInput.disabled = false;
    }
    const directAddButton = card.querySelector('product-form button[name="add"]');
    if (directAddButton) {
      directAddButton.removeAttribute('disabled');
      directAddButton.removeAttribute('aria-disabled');
    }
  }

  async _setProductSwatches(container, product, customFields, targetSwatchContainer = null) {
    try {
      const swatchesContainer = targetSwatchContainer || container.querySelector('[data-product-swatches]');
      if (!swatchesContainer) return;

      // First, check for sibling products GID from custom fields
      const siblingProductsGid = this._getSiblingLinkingGid(customFields);

      // If sibling products exist, use them
      if (siblingProductsGid) {
        const siblingProducts = await this._fetchSiblingProducts(siblingProductsGid);
        if (siblingProducts && siblingProducts.length > 1) {
          const swatchesMarkup = await this._generateSwatchesMarkup(siblingProducts, product, 'sibling');
          swatchesContainer.innerHTML = swatchesMarkup;
          // Attach hover listeners after rendering
          this._attachSwatchHoverListeners(swatchesContainer, container);
          // Defensive: siblings mean the customer has a colour choice, so ensure the
          // card shows Quick Shop even if the CTA heuristic classified it otherwise.
          this._ensureQuickShopCta(container);
          return;
        }
      }

      // Otherwise, check for color variants within the same product
      const productHandle = product.url?.split('/').slice(-1)[0] || '';
      if (productHandle) {
        const variants = await this._fetchProductVariants(productHandle);
        if (variants && variants.length > 1) {
          const swatchesMarkup = await this._generateSwatchesMarkup(variants, product, 'variant');
          swatchesContainer.innerHTML = swatchesMarkup;
          // Attach hover listeners after rendering
          this._attachSwatchHoverListeners(swatchesContainer, container);
          return;
        }
      }

      // No real choice: no >1 siblings and no >1 colour variants. If this card was
      // optimistically forced to Quick Shop by a self-referential sibling link,
      // revert it to Add to Bag.
      if (siblingProductsGid) {
        this._revertToAddToBagIfBase(container, product);
      }
    } catch (error) {
      console.error('Error setting product swatches:', error);
    }
  }

  /**
   * Fetch sibling products from Shopify metaobject
   */
  async _fetchSiblingProducts(gid) {
    try {
      if (!window.storefrontAccessToken) {
        console.warn('Storefront access token not configured - cannot fetch sibling products');
        return [];
      }

      // First, get the metaobject to retrieve the product GIDs
      const metaobjectQuery = `
        query getSiblingProductGids($id: ID!) {
          metaobject(id: $id) {
            fields {
              key
              value
            }
          }
        }
      `;

      const metaobjectResponse = await fetch('/api/2025-07/graphql.json', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': window.storefrontAccessToken
        },
        body: JSON.stringify({
          query: metaobjectQuery,
          variables: { id: gid }
        })
      });

      const metaobjectData = await metaobjectResponse.json();

      if (!metaobjectResponse.ok) {
        throw new Error(`HTTP error! status: ${metaobjectResponse.status}`);
      }

      if (metaobjectData.errors) {
        console.error('GraphQL errors:', metaobjectData.errors);
        return [];
      }

      // Find the linked_products field and extract the product GIDs
      const linkedProductsField = metaobjectData.data?.metaobject?.fields?.find(f =>
        f.key === 'linked_products' || f.key === 'products'
      );

      if (!linkedProductsField || !linkedProductsField.value) {
        console.warn('No linked_products field found in metaobject');
        return [];
      }

      // Parse the product GIDs (they should be in the value as a JSON array or string)
      let productGids;
      try {
        productGids = typeof linkedProductsField.value === 'string'
          ? JSON.parse(linkedProductsField.value)
          : linkedProductsField.value;
      } catch (e) {
        console.error('Error parsing linked_products value:', e);
        return [];
      }

      if (!Array.isArray(productGids) || productGids.length === 0) {
        console.warn('No product GIDs found in linked_products field');
        return [];
      }

      // Now fetch the actual products using their GIDs
      const productsQuery = `
        query getSiblingProducts($ids: [ID!]!) {
          nodes(ids: $ids) {
            ... on Product {
              id
              handle
              title
              availableForSale
              featuredImage {
                url
                altText
              }
              options {
                name
                values
              }
              variants(first: 1) {
                nodes {
                  id
                  barcode
                  metafield_colour_swatch: metafield(namespace: "creative_force", key: "colour_swatch") {
                    value
                  }
                  metafield_item_number: metafield(namespace: "d365", key: "item_number") {
                    value
                  }
                  metafield_fenwick_colour: metafield(namespace: "d365", key: "fenwick_colour") {
                    value
                  }
                }
              }
            }
          }
        }
      `;

      const productsResponse = await fetch('/api/2025-07/graphql.json', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': window.storefrontAccessToken
        },
        body: JSON.stringify({
          query: productsQuery,
          variables: { ids: productGids }
        })
      });

      const productsData = await productsResponse.json();

      if (!productsResponse.ok) {
        throw new Error(`HTTP error! status: ${productsResponse.status}`);
      }

      if (productsData.errors) {
        console.error('GraphQL errors:', productsData.errors);
        return [];
      }

      const products = productsData.data?.nodes?.filter(node => node !== null) || [];

      // Collect all MediaImage GIDs from colour_swatch metafields
      const mediaImageGids = [];
      products.forEach(product => {
        const variant = product.variants?.nodes?.[0];
        const swatchImageGid = variant?.metafield_colour_swatch?.value;
        if (swatchImageGid && swatchImageGid.startsWith('gid://shopify/MediaImage/')) {
          mediaImageGids.push(swatchImageGid);
        }
      });

      // Fetch the actual image URLs if we have any MediaImage GIDs
      let mediaImages = {};
      if (mediaImageGids.length > 0) {
        const mediaQuery = `
          query getMediaImages($ids: [ID!]!) {
            nodes(ids: $ids) {
              ... on MediaImage {
                id
                image {
                  url
                  altText
                }
              }
            }
          }
        `;

        const mediaResponse = await fetch('/api/2025-07/graphql.json', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': window.storefrontAccessToken
          },
          body: JSON.stringify({
            query: mediaQuery,
            variables: { ids: mediaImageGids }
          })
        });

        const mediaData = await mediaResponse.json();

        if (!mediaData.errors) {
          // Create a mapping of MediaImage GID to image URL
          mediaData.data?.nodes?.forEach(node => {
            if (node && node.image) {
              mediaImages[node.id] = node.image.url;
            }
          });
        }
      }

      // Add the actual image URLs to the products
      products.forEach(product => {
        const variant = product.variants?.nodes?.[0];
        if (variant?.metafield_colour_swatch?.value) {
          const mediaImageGid = variant.metafield_colour_swatch.value;
          if (mediaImages[mediaImageGid]) {
            variant.metafield_colour_swatch.imageUrl = mediaImages[mediaImageGid];
          }
        }
      });

      return products;
    } catch (error) {
      // Silently handle aborted requests (user navigated away)
      if (error?.name === 'AbortError' || error?.message?.includes('aborted')) {
        return [];
      }
      // Log other errors
      console.error('Error fetching sibling products:', error);
      return [];
    }
  }

  /**
   * Fetch product variants with color option from Shopify Storefront API
   */
  async _fetchProductVariants(productHandle) {
    try {
      if (!window.storefrontAccessToken) {
        console.warn('Storefront access token not configured - cannot fetch product variants');
        return [];
      }

      // Query to get all variants of a product with color option
      const variantsQuery = `
        query getProductVariants($handle: String!) {
          product(handle: $handle) {
            id
            handle
            title
            availableForSale
            options {
              name
              values
            }
            variants(first: 100) {
              nodes {
                id
                title
                availableForSale
                barcode
                image {
                  url
                  altText
                }
                selectedOptions {
                  name
                  value
                }
                metafield_colour_swatch: metafield(namespace: "creative_force", key: "colour_swatch") {
                  value
                }
                metafield_item_number: metafield(namespace: "d365", key: "item_number") {
                  value
                }
                metafield_fenwick_colour: metafield(namespace: "d365", key: "fenwick_colour") {
                  value
                }
              }
            }
          }
        }
      `;

      const variantsResponse = await fetch('/api/2025-07/graphql.json', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': window.storefrontAccessToken
        },
        body: JSON.stringify({
          query: variantsQuery,
          variables: { handle: productHandle }
        })
      });

      const variantsData = await variantsResponse.json();

      if (!variantsResponse.ok) {
        throw new Error(`HTTP error! status: ${variantsResponse.status}`);
      }

      if (variantsData.errors) {
        console.error('GraphQL errors:', variantsData.errors);
        return [];
      }

      const product = variantsData.data?.product;
      if (!product) {
        console.warn('Product not found');
        return [];
      }

      // Find the color option (could be named Colour, Color, etc.)
      const colorOption = product.options?.find(opt =>
        opt.name.toLowerCase().includes('colour') || opt.name.toLowerCase().includes('color')
      );

      if (!colorOption) {
        // No color option, return empty array
        return [];
      }

      const variants = product.variants?.nodes || [];

      // Filter variants to only include unique color values
      const seenColors = new Set();
      const uniqueColorVariants = variants.filter(variant => {
        const colorValue = variant.selectedOptions?.find(opt =>
          opt.name.toLowerCase().includes('colour') || opt.name.toLowerCase().includes('color')
        )?.value;

        if (!colorValue || seenColors.has(colorValue.toLowerCase())) {
          return false;
        }
        seenColors.add(colorValue.toLowerCase());
        return true;
      });

      // Collect all MediaImage GIDs from colour_swatch metafields
      const mediaImageGids = [];
      uniqueColorVariants.forEach(variant => {
        const swatchImageGid = variant?.metafield_colour_swatch?.value;
        if (swatchImageGid && swatchImageGid.startsWith('gid://shopify/MediaImage/')) {
          mediaImageGids.push(swatchImageGid);
        }
      });

      // Fetch the actual image URLs if we have any MediaImage GIDs
      let mediaImages = {};
      if (mediaImageGids.length > 0) {
        const mediaQuery = `
          query getMediaImages($ids: [ID!]!) {
            nodes(ids: $ids) {
              ... on MediaImage {
                id
                image {
                  url
                  altText
                }
              }
            }
          }
        `;

        const mediaResponse = await fetch('/api/2025-07/graphql.json', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': window.storefrontAccessToken
          },
          body: JSON.stringify({
            query: mediaQuery,
            variables: { ids: mediaImageGids }
          })
        });

        const mediaData = await mediaResponse.json();

        if (!mediaData.errors) {
          // Create a mapping of MediaImage GID to image URL
          mediaData.data?.nodes?.forEach(node => {
            if (node && node.image) {
              mediaImages[node.id] = node.image.url;
            }
          });
        }
      }

      // Add the actual image URLs to the variants and transform to match sibling product structure
      const transformedVariants = uniqueColorVariants.map(variant => {
        if (variant?.metafield_colour_swatch?.value) {
          const mediaImageGid = variant.metafield_colour_swatch.value;
          if (mediaImages[mediaImageGid]) {
            variant.metafield_colour_swatch.imageUrl = mediaImages[mediaImageGid];
          }
        }

        // Transform variant to match sibling product structure
        return {
          id: variant.id,
          handle: product.handle,
          title: variant.title,
          availableForSale: variant.availableForSale,
          isVariant: true, // Flag to distinguish from sibling products
          variantId: variant.id.split('/').pop(), // Extract numeric ID
          selectedOptions: variant.selectedOptions,
          metafield_colour_swatch: variant.metafield_colour_swatch,
          metafield_item_number: variant.metafield_item_number,
          metafield_fenwick_colour: variant.metafield_fenwick_colour,
          barcode: variant.barcode,
          image: variant.image // Include variant image
        };
      });

      return transformedVariants;
    } catch (error) {
      // Silently handle aborted requests (user navigated away)
      if (error?.name === 'AbortError' || error?.message?.includes('aborted')) {
        return [];
      }
      // Log other errors
      console.error('Error fetching product variants:', error);
      return [];
    }
  }

  /**
   * Generate swatches markup for sibling products or product variants
   * @param {Array} items - Array of sibling products or variants
   * @param {Object} currentProduct - Current Nosto product object
   * @param {String} type - 'sibling' or 'variant'
   */
  /**
   * Normalise an image URL to a comparable key (pathname without query), so
   * variant images can be matched regardless of width/version query params.
   * @param {string} url
   * @returns {string}
   */
  _imagePathKey(url) {
    if (!url) return '';
    try {
      return new URL(url, window.location.origin).pathname;
    } catch (_) {
      return String(url).split('?')[0];
    }
  }

  /**
   * Determine which variant a variant-product card is currently showing so only
   * that swatch is highlighted on load. Prefers the variant whose image matches
   * the card's displayed image, then the product's default sku, then the first
   * available variant.
   * @param {Array} variants
   * @param {Object} product - The Nosto product object
   * @returns {(string|number|null)} The current variant id, or null.
   */
  _resolveCurrentVariantId(variants, product) {
    const available = (variants || []).filter(v => v.availableForSale);
    if (!available.length) return null;

    const currentImageKey = this._imagePathKey(product?.imageUrl || product?.image_url);
    if (currentImageKey) {
      const byImage = available.find(v => this._imagePathKey(v.image?.url) === currentImageKey);
      if (byImage) return byImage.variantId;
    }

    const defaultSkuId = product?.skus?.[0]?.id;
    if (defaultSkuId != null) {
      const bySku = available.find(v => String(v.variantId) === String(defaultSkuId));
      if (bySku) return bySku.variantId;
    }

    return available[0].variantId;
  }

  async _generateSwatchesMarkup(items, currentProduct, type = 'sibling') {
    const MAX_VISIBLE_SWATCHES = 3;
    let totalEligibleSwatches = 0;

    // For variant products every swatch shares the same product handle, so we
    // can't use the handle to find the active colour. Resolve the single current
    // variant up-front (matched to the image the card is showing) instead.
    const currentVariantId = type === 'variant'
      ? this._resolveCurrentVariantId(items, currentProduct)
      : null;

    const swatches = items.map(item => {
      let colorValue, isCurrentItem, isAvailable, swatchImage, ean, itemNumber, fenwickColour;

      if (type === 'variant') {
        // Handle regular product variants
        colorValue = item.selectedOptions?.find(opt =>
          opt.name.toLowerCase().includes('colour') || opt.name.toLowerCase().includes('color')
        )?.value;

        if (!colorValue) return null;

        isCurrentItem = currentVariantId != null && String(item.variantId) === String(currentVariantId);
        isAvailable = item.availableForSale;

        // Skip sold-out variants
        if (!isAvailable) return null;
        totalEligibleSwatches++;

        // Get swatch data from variant metafields
        swatchImage = item.metafield_colour_swatch?.imageUrl || null;
        ean = item.barcode;
        itemNumber = item.metafield_item_number?.value;
        fenwickColour = item.metafield_fenwick_colour?.value;

        // Generate swatch HTML for variant
        return this._generateSingleSwatchMarkup({
          item,
          colorValue,
          swatchImage,
          ean,
          itemNumber,
          fenwickColour,
          isCurrentItem,
          isAvailable,
          type: 'variant'
        });
      } else {
        // Handle sibling products (original logic)
        const colorOption = item.options?.find(opt =>
          opt.name.toLowerCase().includes('colour') || opt.name.toLowerCase().includes('color')
        );

        if (!colorOption || !colorOption.values.length) return null;

        colorValue = colorOption.values[0];
        const variant = item.variants?.nodes?.[0];
        // Derive the handle from the Nosto product URL (the product object doesn't
        // reliably expose `.handle`), so the current colour is highlighted on load.
        const currentSiblingHandle = currentProduct.url?.split('/').slice(-1)[0] || currentProduct.handle;
        isCurrentItem = item.handle === currentSiblingHandle;
        isAvailable = item.availableForSale;

        // Skip sold-out products
        if (!isAvailable) return null;
        totalEligibleSwatches++;

        // Get swatch data from variant metafields
        swatchImage = variant?.metafield_colour_swatch?.imageUrl || null;
        ean = variant?.barcode;
        itemNumber = variant?.metafield_item_number?.value;
        fenwickColour = variant?.metafield_fenwick_colour?.value;

        // Generate swatch HTML for sibling product
        return this._generateSingleSwatchMarkup({
          item,
          colorValue,
          swatchImage,
          ean,
          itemNumber,
          fenwickColour,
          isCurrentItem,
          isAvailable,
          type: 'sibling'
        });
      }
    }).filter(Boolean);

    if (swatches.length > 1) {
      const visibleSwatches = swatches.slice(0, MAX_VISIBLE_SWATCHES);
      const remainingCount = Math.max(0, totalEligibleSwatches - MAX_VISIBLE_SWATCHES);
      const moreIndicator = remainingCount > 0
        ? `<span class="swatches-more" aria-label="${remainingCount} more colours">+${remainingCount}</span>`
        : '';

      return `
        <div class="nosto-swatches-container fade-in">
          <span class="visually-hidden">Available in</span>
          <div class="swatches swatches--mobile flex" style="gap: 8px;">
            ${visibleSwatches.join('')}
            ${moreIndicator}
          </div>
          <div class="swatches swatches--desktop flex" style="gap: 8px;">
            ${visibleSwatches.join('')}
            ${moreIndicator}
          </div>
        </div>
      `;
    }

    return '';
  }

  /**
   * Generate single swatch markup
   * @param {Object} params - Parameters object
   * @param {Object} params.item - Product or variant object
   * @param {String} params.colorValue - Color value to display
   * @param {String} params.swatchImage - Swatch image URL
   * @param {String} params.ean - EAN barcode
   * @param {String} params.itemNumber - Item number
   * @param {String} params.fenwickColour - Fenwick colour code
   * @param {Boolean} params.isCurrentItem - Whether this is the current item
   * @param {Boolean} params.isAvailable - Whether the item is available
   * @param {String} params.type - 'sibling' or 'variant'
   */
  _generateSingleSwatchMarkup({ item, colorValue, swatchImage, ean, itemNumber, fenwickColour, isCurrentItem, isAvailable, type = 'sibling' }) {
    let productUrl;

    if (type === 'variant') {
      // For variants, link to the same product with variant query parameter
      productUrl = `/products/${item.handle}?variant=${item.variantId}`;
    } else {
      // For sibling products, link to the different product
      productUrl = `/products/${item.handle}`;
    }

    const linkClass = `swatch ${isCurrentItem ? 'current-product' : ''} ${!isAvailable ? 'sold-out' : ''}`;

    // Get the image data for hover functionality
    let imageUrl = '';
    let imageAlt = '';

    if (type === 'variant') {
      // For variants, use the variant image
      imageUrl = item.image?.url || '';
      imageAlt = item.image?.altText || colorValue;
    } else {
      // For sibling products, use the featured image
      imageUrl = item.featuredImage?.url || '';
      imageAlt = item.featuredImage?.altText || item.title || colorValue;
    }

    // Determine swatch display method (following your existing logic)
    let swatchContent = '';

    if (swatchImage) {
      swatchContent = `
        <img
          src="${swatchImage}?width=110&height=110"
          alt="${colorValue}"
          title="${colorValue}"
          class="color"
          width="34"
          height="34"
          loading="lazy"
          fetchpriority="low"
          data-metafield-swatch
        >
      `;
    } else {
      // No swatch image available - skip this swatch
      return null;
    }

    // Extract numeric product ID for modal targeting
    let productId = '';
    if (type === 'sibling' && item.id) {
      // GID format: gid://shopify/Product/12345678
      const idMatch = item.id.match(/(\d+)$/);
      productId = idMatch ? idMatch[1] : '';
    }

    // Add data attributes for click image preview and link update
    const imageDataAttrs = imageUrl ? `data-hover-image="${imageUrl}" data-hover-image-alt="${imageAlt}"` : '';
    const productDataAttrs = `data-product-url="${productUrl}" data-product-handle="${item.handle}"${productId ? ` data-product-id="${productId}"` : ''}`;

    const linkElement = isAvailable ?
      `<a href="${productUrl}" class="${linkClass}" ${imageDataAttrs} ${productDataAttrs} tabindex="-1">` :
      `<div class="${linkClass}" ${imageDataAttrs} ${productDataAttrs}>`;

    const closingElement = isAvailable ? '</a>' : '</div>';

    return `
      ${linkElement}
        ${swatchContent}
        <span class="visually-hidden">${colorValue}</span>
      ${closingElement}
    `;
  }

  /**
   * Attach click event listeners to swatches for image preview and link update.
   * Clicking a swatch keeps the user on the PLP, updates the card image to the
   * selected colour, and re-points all card links (including CTA) to the
   * chosen variant/sibling PDP.
   * @param {HTMLElement} swatchesContainer - The container with the swatches
   * @param {HTMLElement} productCard - The product card containing the image
   */
  _attachSwatchHoverListeners(swatchesContainer, productCard) {
    const swatches = swatchesContainer.querySelectorAll('.swatch[data-hover-image]');
    if (!swatches.length) return;

    // Find the primary image element in the product card
    const cardMedia = productCard.querySelector('.card-media');
    if (!cardMedia) return;

    const primaryImage = cardMedia.querySelector('img');
    if (!primaryImage) return;

    // Add click listener to each swatch
    swatches.forEach(swatch => {
      swatch.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        const imageUrl = swatch.getAttribute('data-hover-image');
        const imageAlt = swatch.getAttribute('data-hover-image-alt') || '';
        const rawProductUrl = swatch.getAttribute('data-product-url') || '';
        const productUrl = this.sanitizeProductUrl(rawProductUrl);
        const productHandle = swatch.getAttribute('data-product-handle') || '';

        if (!imageUrl) return;

        let safeImageUrl;
        try {
          const parsedUrl = new URL(imageUrl, window.location.origin);
          if (parsedUrl.protocol !== 'http:' && parsedUrl.protocol !== 'https:') return;
          safeImageUrl = parsedUrl.toString();
        } catch (_) {
          return;
        }

        // --- Update card image ---
        const srcset = [
          `${safeImageUrl}&width=90 90w`,
          `${safeImageUrl}&width=180 180w`,
          `${safeImageUrl}&width=280 280w`,
          `${safeImageUrl}&width=390 390w`,
          `${safeImageUrl}&width=540 540w`,
          `${safeImageUrl}&width=768 768w`,
          `${safeImageUrl}&width=1080 1080w`,
          `${safeImageUrl}&width=1280 1280w`,
          `${safeImageUrl}&width=1500 1500w`
        ].join(', ');
        primaryImage.setAttribute('srcset', srcset);
        primaryImage.setAttribute('src', `${safeImageUrl}&width=540`);
        primaryImage.setAttribute('alt', imageAlt);

        // --- Update the carousel image array so hover/leave doesn't show old images ---
        const card = swatch.closest('.card--product');
        if (card) {
          // Immediately set the swatch image as the only carousel image to prevent
          // glitches while we fetch the full image set asynchronously.
          const newUrls = [safeImageUrl];
          card._imgUrls = newUrls;
          card.dataset.imgUrls = JSON.stringify(newUrls);
          card.dataset.imgIndex = '0';
          card._carouselPreloaded = false;

          // Both swatch types resolve their carousel from the product's image
          // list, but differently:
          //  - Siblings are separate products, so /products/{handle}.js returns
          //    that colour's own images — restore the first N as before.
          //  - Normal colour variants share one product whose images cover every
          //    colour. Find the selected variant image in that list and assume the
          //    image immediately after it is the matching hover shot, so hover/leave
          //    stay on the selected colour.
          const isSiblingSwatch = Boolean(swatch.getAttribute('data-product-id'));

          if (productHandle) {
            fetch(`/products/${productHandle}.js`)
              .then(res => res.json())
              .then(productData => {
                if (!productData || !Array.isArray(productData.images) || productData.images.length === 0) return;

                // Strip query params so URLs compare consistently and so the
                // carousel can re-append its own &width= sizing.
                const fullUrls = productData.images.map(img => img.replace(/\?.*$/, ''));

                let newImgUrls;

                if (isSiblingSwatch) {
                  // Apply the same image cap as initial card rendering:
                  // fashion collections show all images, others cap at 2.
                  const isFashion = this._isFashionCollection();
                  newImgUrls = (!isFashion && fullUrls.length > 2) ? fullUrls.slice(0, 2) : fullUrls;
                } else {
                  // Locate the selected variant image (compare by path, ignoring
                  // query/size) and pair it with the next image as the hover shot.
                  const toPath = (url) => {
                    try { return new URL(url, window.location.origin).pathname; }
                    catch (_) { return url.replace(/\?.*$/, ''); }
                  };
                  const selectedPath = toPath(safeImageUrl);
                  const selectedIndex = fullUrls.findIndex(url => toPath(url) === selectedPath);

                  if (selectedIndex === -1) {
                    // Couldn't locate it — keep just the selected colour image.
                    newImgUrls = [safeImageUrl.replace(/\?.*$/, '')];
                  } else {
                    newImgUrls = [fullUrls[selectedIndex]];
                    if (selectedIndex + 1 < fullUrls.length) {
                      newImgUrls.push(fullUrls[selectedIndex + 1]);
                    }
                  }
                }

                card._imgUrls = newImgUrls;
                card.dataset.imgUrls = JSON.stringify(newImgUrls);
                card.dataset.imgCount = String(newImgUrls.length);
                card.dataset.imgIndex = '0';

                // Proactively warm the non-primary images (esp. the hover shot)
                // now, using the card's real srcset/sizes, so the first hover
                // after a swatch click is instant instead of waiting on a fetch.
                for (let i = 1; i < newImgUrls.length; i++) {
                  this._preloadCarouselImage(card, newImgUrls[i]);
                }
                card._carouselPreloaded = true;

                // Rebuild indicator segments to match new image count
                const indicator = card.querySelector('[data-img-indicator]');
                if (indicator) {
                  indicator.innerHTML = newImgUrls
                    .map((_, i) => `<span class="card-img-indicator-segment${i === 0 ? ' active' : ''}" data-img-segment="${i}"></span>`)
                    .join('');
                }
              })
              .catch(() => { /* Silently keep single-image fallback */ });
          }
        }

        // --- Update all card links to the selected product/variant URL ---
        if (productUrl && card) {
            // Update the card media link
            const mediaLink = card.querySelector('.card-media');
            if (mediaLink && mediaLink.tagName === 'A') {
              mediaLink.setAttribute('href', productUrl);
            }

            // Update the card title link
            const titleLink = card.querySelector('.card-title a');
            if (titleLink) {
              titleLink.setAttribute('href', productUrl);
            }

            // Update quick-buy element so it opens the correct modal
            const quickBuyEl = card.querySelector('quick-buy');
            if (quickBuyEl && productHandle) {
              quickBuyEl.setAttribute('data-url', productUrl);

              // Update the instance properties that quick-buy caches in constructor
              quickBuyEl.quickBuyProductHandle = productHandle;
              quickBuyEl.processingModal = false;

              // If we have a product ID (sibling), update the target modal ID
              const swatchProductId = swatch.getAttribute('data-product-id');
              const isValidProductId = typeof swatchProductId === 'string' && /^[A-Za-z0-9_-]+$/.test(swatchProductId);
              if (isValidProductId) {
                const newModalId = `modal-${swatchProductId}-product`;
                quickBuyEl.dataset.product = swatchProductId;
                quickBuyEl.targetModalId = newModalId;
                const quickBuyLink = quickBuyEl.querySelector('a[data-product-handle]');
                if (quickBuyLink) {
                  quickBuyLink.setAttribute('data-product-handle', productHandle);
                  quickBuyLink.setAttribute('href', `#${newModalId}`);
                }
              } else {
                const quickBuyLink = quickBuyEl.querySelector('a[data-product-handle]');
                if (quickBuyLink) {
                  quickBuyLink.setAttribute('data-product-handle', productHandle);
                }
              }

              // Remove any previously cached modal so it fetches fresh
              document.querySelectorAll('[data-quick-buy-modal]').forEach((m) => m.remove());
            }
        }

        // --- Mark clicked swatch as active ---
        // Compare against the raw attribute value (relative path). `productUrl`
        // is sanitized to an absolute href and would never match the relative
        // data-product-url attribute, so the active class was never applied.
        if (card) {
          const activeUrl = swatch.getAttribute('data-product-url');
          // Toggling covers the matching swatch in both the mobile & desktop
          // containers (they share the same data-product-url).
          card.querySelectorAll('.nosto-product-swatches .swatch').forEach(s => {
            s.classList.toggle('current-product', s.getAttribute('data-product-url') === activeUrl);
          });
        }
      });
    });
  }

  /**
   * Find swatch image URL by EAN or item number
   */
  _findSwatchImageUrl(ean, itemNumber) {
    // Check if images exist in the global images object (if available)
    if (window.images) {
      let url = ean ? `${ean}_swatch.png` : null;
      if (url && window.images[url]) {
        return window.images[url];
      }

      url = ean ? `${ean}_swatch.jpg` : null;
      if (url && window.images[url]) {
        return window.images[url];
      }

      url = itemNumber ? `${itemNumber}_swatch.png` : null;
      if (url && window.images[url]) {
        return window.images[url];
      }

      url = itemNumber ? `${itemNumber}_swatch.jpg` : null;
      if (url && window.images[url]) {
        return window.images[url];
      }
    }

    return null;
  }

  /**
   * Check whether the current collection is in the configured fashion handles list.
   * Fashion collections show all available product images in the carousel;
   * all other collections are capped at 2 images.
   */
  _isFashionCollection() {
    const handles = (this.options.fashionCollectionHandles || '')
      .split(',')
      .map(h => h.trim().toLowerCase())
      .filter(Boolean);
    const currentHandle = (this.options.currentCollectionHandle || '').trim().toLowerCase();
    return handles.length > 0 && Boolean(currentHandle) && handles.includes(currentHandle);
  }

  _isTouchOrSmallViewport() {
    if (!window.matchMedia) return false;
    const smallViewport = window.matchMedia('(max-width: 767px)').matches;
    const noHover = window.matchMedia('(hover: none)').matches;
    const coarsePointer = window.matchMedia('(pointer: coarse)').matches;
    return smallViewport || (noHover && coarsePointer);
  }

  _placeTouchCardCta(cardBody, cta) {
    // Always last in the card body so the CTA stays a direct flex child.
    // Inserting after `.yotpo-widget-instance` is unsafe: after hydration that
    // node can be nested, which pulls Quick Shop into Yotpo's own flex row.
    if (cta.parentElement === cardBody && cardBody.lastElementChild === cta) return;
    cardBody.appendChild(cta);
  }

  _relocateCardCtaForTouchDevices(container) {
    if (!this._isTouchOrSmallViewport()) return;

    const card = container.querySelector('.card--product');
    const cta = container.querySelector('[data-card-cta]');
    const cardBody = container.querySelector('.card-body');
    if (!card || !cta || !cardBody) return;

    this._placeTouchCardCta(cardBody, cta);
    card.classList.add('card--touch-static-cta');
  }

  _syncTouchCardCtasInGrid() {
    if (!this.productGrid) return;

    const touchMode = this._isTouchOrSmallViewport();
    const cards = this.productGrid.querySelectorAll('.card--product');

    cards.forEach((card) => {
      const cta = card.querySelector('[data-card-cta]');
      const cardBody = card.querySelector('.card-body');
      const mediaWrap = card.querySelector('.card-media-wrap');
      if (!cta || !cardBody || !mediaWrap) return;

      if (touchMode) {
        this._placeTouchCardCta(cardBody, cta);
        card.classList.add('card--touch-static-cta');
      } else {
        if (!mediaWrap.contains(cta)) {
          mediaWrap.appendChild(cta);
        }
        card.classList.remove('card--touch-static-cta');
      }
    });
  }

  _withWidth(url, width) {
    if (!url) return '';
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}width=${width}`;
  }

  _preloadImage(url) {
    if (!url || this.preloadedImageUrls.has(url)) return;
    this.preloadedImageUrls.add(url);
    const img = new Image();
    img.decoding = 'async';
    img.src = this._withWidth(url, 540);
  }

  /**
   * Warm a carousel image using the same srcset/sizes the card renders with, so
   * the browser fetches (and caches) the exact responsive candidate it will
   * display. Used after a swatch click so hovering to the next image is instant
   * rather than triggering a fresh network request.
   * @param {HTMLElement} card - The .card--product element
   * @param {string} url - Base image URL (no width query)
   */
  _preloadCarouselImage(card, url) {
    if (!url || this.preloadedImageUrls.has(url)) return;
    this.preloadedImageUrls.add(url);

    const cardImg = card.querySelector('.card-media img');
    const img = new Image();
    img.decoding = 'async';
    img.sizes = cardImg?.getAttribute('sizes') || '100vw';
    img.srcset = [90, 180, 280, 390, 540, 768, 1080, 1280, 1500]
      .map((w) => `${this._withWidth(url, w)} ${w}w`)
      .join(', ');
    img.src = this._withWidth(url, 540);
  }

  /**
   * Return the cached image URL array for a card, parsing once from the
   * data attribute and storing the result as a JS property for future access.
   */
  _getCardImageUrls(card) {
    if (!card._imgUrls) {
      card._imgUrls = JSON.parse(card.dataset.imgUrls || '[]');
    }
    return card._imgUrls;
  }

  /**
   * Ensure all carousel images for a card are preloaded.
   * Safe to call multiple times — only fires actual preloads once.
   */
  _ensureCarouselPreloaded(card) {
    if (card._carouselPreloaded) return;
    card._carouselPreloaded = true;
    const imgUrls = this._getCardImageUrls(card);
    for (let i = 1; i < imgUrls.length; i++) {
      this._preloadImage(imgUrls[i]);
    }
  }

  _preloadCardCarouselImages(card, fromIndex = 1) {
    const imgUrls = this._getCardImageUrls(card);
    if (!imgUrls.length) return;
    for (let i = fromIndex; i < imgUrls.length; i++) {
      this._preloadImage(imgUrls[i]);
    }
  }

  /**
   * Update the displayed image on a card to the given index.
   * Updates the img src/srcset and the indicator active state.
   * @param {HTMLElement} card - The .card--product element
   * @param {number} index - The image index to show
   */
  _setCardImageIndex(card, index) {
    const imgUrls = this._getCardImageUrls(card);
    if (!imgUrls.length || index < 0 || index >= imgUrls.length) return;

    // Preload all carousel images on first navigation (covers hover, click, and touch)
    this._ensureCarouselPreloaded(card);

    card.dataset.imgIndex = index;
    const img = card.querySelector('.card-media img');
    if (img) {
      const url = imgUrls[index];
      const srcset = [
        `${this._withWidth(url, 90)} 90w`,
        `${this._withWidth(url, 180)} 180w`,
        `${this._withWidth(url, 280)} 280w`,
        `${this._withWidth(url, 390)} 390w`,
        `${this._withWidth(url, 540)} 540w`,
        `${this._withWidth(url, 768)} 768w`,
        `${this._withWidth(url, 1080)} 1080w`,
        `${this._withWidth(url, 1280)} 1280w`,
        `${this._withWidth(url, 1500)} 1500w`
      ].join(', ');
      img.srcset = srcset;
      img.src = this._withWidth(url, 540);
    }

    // Update indicator segments
    card.querySelectorAll('[data-img-segment]').forEach((seg, i) => {
      seg.classList.toggle('active', i === index);
    });
  }

  /**
   * Build indicator segments and attach carousel event listeners for all product
   * cards in the grid. Call this after products are inserted into the DOM.
   */
  _setupAllImageCarousels() {
    const cards = this.productGrid.querySelectorAll('.card--product[data-img-urls]');
    cards.forEach(card => {
      // Skip if already initialised
      if (card.dataset.carouselReady) return;
      card.dataset.carouselReady = 'true';

      const imgUrls = JSON.parse(card.dataset.imgUrls || '[]');
      const imgCount = imgUrls.length;

      // Build indicator segments
      const indicator = card.querySelector('[data-img-indicator]');
      if (indicator) {
        indicator.innerHTML = imgUrls
          .map((_, i) => `<span class="card-img-indicator-segment${i === 0 ? ' active' : ''}" data-img-segment="${i}"></span>`)
          .join('');
      }

      // Nothing more to do for single-image cards
      if (imgCount <= 1) return;

      const prevBtn = card.querySelector('[data-img-prev]');
      const nextBtn = card.querySelector('[data-img-next]');
      const imageContainer = card.querySelector('.card-media-wrap');

      // Auto-advance to secondary image on hover (replicates previous CSS swap)
      if (imageContainer) {
        imageContainer.addEventListener('mouseenter', () => {
          const currentCount = this._getCardImageUrls(card).length;
          if (currentCount > 1 && parseInt(card.dataset.imgIndex || '0') === 0) {
            this._setCardImageIndex(card, 1);
          }
        });
        imageContainer.addEventListener('mouseleave', () => {
          this._setCardImageIndex(card, 0);
        });
      }

      // Arrow navigation — read count dynamically so swatch changes are respected
      if (prevBtn) {
        prevBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          const currentUrls = this._getCardImageUrls(card);
          const count = currentUrls.length;
          if (count <= 1) return;
          const current = parseInt(card.dataset.imgIndex || '0');
          const targetIndex = (current - 1 + count) % count;
          this._setCardImageIndex(card, targetIndex);
        });
      }

      if (nextBtn) {
        nextBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          const currentUrls = this._getCardImageUrls(card);
          const count = currentUrls.length;
          if (count <= 1) return;
          const current = parseInt(card.dataset.imgIndex || '0');
          const targetIndex = (current + 1) % count;
          this._setCardImageIndex(card, targetIndex);
        });
      }

      // Indicator segment click
      if (indicator) {
        indicator.addEventListener('click', (e) => {
          const seg = e.target.closest('[data-img-segment]');
          if (seg) this._setCardImageIndex(card, parseInt(seg.dataset.imgSegment));
        });
      }

      // Touch swipe navigation (mobile/tablet touch devices)
      const mediaWrap = card.querySelector('.card-media-wrap') || card.querySelector('.card-media');
      const supportsTouchSwipe = mediaWrap && window.matchMedia && (
        window.matchMedia('(hover: none)').matches ||
        window.matchMedia('(pointer: coarse)').matches
      );

      if (supportsTouchSwipe) {
        let startX = 0;
        let startY = 0;
        let deltaX = 0;
        let deltaY = 0;
        let isTouchActive = false;
        let hasHorizontalIntent = false;

        const SWIPE_THRESHOLD = 32;
        const INTENT_THRESHOLD = 8;

        mediaWrap.addEventListener('touchstart', (e) => {
          if (!e.touches || e.touches.length !== 1) return;
          const touch = e.touches[0];
          startX = touch.clientX;
          startY = touch.clientY;
          deltaX = 0;
          deltaY = 0;
          isTouchActive = true;
          hasHorizontalIntent = false;
        }, { passive: true });

        mediaWrap.addEventListener('touchmove', (e) => {
          if (!isTouchActive || !e.touches || e.touches.length !== 1) return;

          const touch = e.touches[0];
          deltaX = touch.clientX - startX;
          deltaY = touch.clientY - startY;

          if (!hasHorizontalIntent && Math.abs(deltaX) > INTENT_THRESHOLD) {
            hasHorizontalIntent = Math.abs(deltaX) > Math.abs(deltaY);
          }

          // Prevent page vertical scrolling when user is intentionally swiping the carousel.
          if (hasHorizontalIntent) {
            e.preventDefault();
          }
        }, { passive: false });

        mediaWrap.addEventListener('touchend', () => {
          if (!isTouchActive) return;
          isTouchActive = false;

          if (!hasHorizontalIntent || Math.abs(deltaX) < SWIPE_THRESHOLD) return;

          const currentUrls = this._getCardImageUrls(card);
          const count = currentUrls.length;
          if (count <= 1) return;
          const current = parseInt(card.dataset.imgIndex || '0');
          const targetIndex = deltaX < 0
            ? (current + 1) % count
            : (current - 1 + count) % count;

          this._setCardImageIndex(card, targetIndex);
          card.dataset.suppressCardClick = 'true';
          setTimeout(() => {
            card.dataset.suppressCardClick = 'false';
          }, 250);
        }, { passive: true });

        // Avoid opening the product page immediately after a swipe gesture.
        card.addEventListener('click', (e) => {
          if (card.dataset.suppressCardClick !== 'true') return;
          e.preventDefault();
          e.stopPropagation();
        }, true);
      }
    });
  }

  /**
   * Set loading state for this placement
   */
  setLoadingState() {
    if (this.loadingSpinner) {
      this.loadingSpinner.classList.remove('hidden');
    }
    if (this.container) {
      this.container.setAttribute('loading', '');
    }
    if (this.productGrid) {
      this.productGrid.setAttribute('loading', '');
    }
  }

  /**
   * Remove loading state for this placement
   */
  removeLoadingState() {
    if (this.loadingSpinner) {
      this.loadingSpinner.classList.add('hidden');
    }
    if (this.container) {
      this.container.removeAttribute('loading');
    }
    if (this.productGrid) {
      this.productGrid.removeAttribute('loading');
    }
  }

  /**
   * Hide the wrapper element for this placement
   */
  hideWrapper() {
    if (this.container) {
      this.container.classList.add('hidden');
    }
    if (this.productGrid) {
      this.productGrid.classList.add('hidden');
    }
  }

  /**
   * Set up click tracking for product cards using event delegation
   */
  setupClickTracking() {
    if (this.productGrid) {
      document.addEventListener('quickBuy:modalOpened', (event) => {
        const productId = event.detail.productId;
        const productUrl = event.detail.productUrl;
        if (productUrl && productId) {
          this.trackSearchClick(productId, productUrl);
        }
      });
      this.productGrid.addEventListener('click', this.handleProductClick.bind(this));
    }
  }

  /**
   * Handle click events on product cards and track via Nosto API
   * @param {Event} event - Click event
   */
  handleProductClick(event) {
    if (event.target.closest('quick-buy')) return;
    const card = event.target.classList.contains('card--product') ? event.target : event.target.closest('.card--product');
    if (!card) return;

    const productId = card.getAttribute('data-product-id') || card.getAttribute('data-track-product-id');
    const productUrl = `${window.location.origin}${card.querySelector('a')?.getAttribute('href')}`;

    if (productUrl && productId) {
      this.trackSearchClick(productId, productUrl);
    }
  }

  /**
   * Track when a product in search results is clicked
   * @param {string} productId - ID of the product that was clicked
   * @param {string} productUrl - URL of the product page
   */
  trackSearchClick(productId, productUrl) {
    if (!window.nostojs) return;
    nostojs(api => {
      api.recordSearchClick(
        this.context,
        {
          productId: productId,
          url: productUrl
        }
      );
    });
  }

  updatePaginationState() {
    const total = parseInt(this.productGrid?.getAttribute('data-filtered-count')) || 0;
    const totalPages = Math.ceil(total / this.resultsSize);

    // Update SEO pagination links
    const seoPagination = document.querySelector('.seo-pagination');
    if (seoPagination) {
      seoPagination.innerHTML = this.generatePaginationLinks(this.currentPage, totalPages);
    }

    // Update meta tags
    this.updatePaginationMeta(this.currentPage, totalPages);
  }

  generatePaginationLinks(currentPage, totalPages) {
    const baseUrl = window.location.pathname;
    const params = new URLSearchParams(window.location.search);
    params.delete('view')
    let links = '';

    if (currentPage > 1) {
      if (currentPage - 1 === 1) {
        params.delete('page');
      } else {
        params.set('page', currentPage - 1);
      }
      const paramsStr = params.toString() ? `?${params.toString()}` : '';
      links += `<a href="${baseUrl}${paramsStr}" rel="prev">Previous</a>`;
    }

    if (currentPage < totalPages) {
      params.set('page', currentPage + 1);
      const paramsStr = params.toString() ? `?${params.toString()}` : '';
      links += `<a href="${baseUrl}${paramsStr}" rel="next">Next</a>`;
    }

    return links;
  }

  updatePaginationMeta(currentPage, totalPages) {
    // Remove existing rel meta tags
    document.querySelectorAll('link[rel="prev"], link[rel="next"]').forEach(el => el.remove());

    const baseUrl = window.location.pathname;
    const params = new URLSearchParams(window.location.search);
    params.delete('view');

    // Add prev meta tag if not on first page
    if (currentPage > 1) {
      if (currentPage - 1 === 1) {
        params.delete('page');
      } else {
        params.set('page', currentPage - 1);
      }
      const paramsStr = params.toString() ? `?${params.toString()}` : '';
      const prevLink = document.createElement('link');
      prevLink.rel = 'prev';
      prevLink.href = `${baseUrl}${paramsStr}`;
      document.head.appendChild(prevLink);
    }

    // Add next meta tag if not on last page
    if (currentPage < totalPages) {
      params.set('page', currentPage + 1);
      const paramsStr = params.toString() ? `?${params.toString()}` : '';
      const nextLink = document.createElement('link');
      nextLink.rel = 'next';
      nextLink.href = `${baseUrl}${paramsStr}`;
      document.head.appendChild(nextLink);
    }
  }

  /**
   * Set up initial state from URL parameters
   */
  initializeFromURL() {
    const params = new URLSearchParams(window.location.search);
    this.currentPage = parseInt(params.get('page')) || 1;

    // Handle keyword parameter
    const isKeyword = params.get('keyword') === 'true';
    if (isKeyword) {
      params.set('keyword', 'true');
    }

    // Update URL and sort options
    this.updateQuery(params);
    if (params.has('sort_by') && this.sortBy) {
      this.sortBy.value = params.get('sort_by');
    }

    // Set loading state and fetch results
    this.setLoadingState();
    this.getSearchResults();
    this.updatePaginationState();
  }

  /**
   * Set up all event listeners for the search component
   */
  setupEventListeners() {
    // Store bound event handlers as class properties
    this.boundFacetChangeHandler = this.onFacetChange.bind(this);
    this.boundFacetClearHandler = this.onFacetClear.bind(this);

    // Use optional chaining to prevent errors if elements don't exist
    this.searchForm?.addEventListener('submit', this.onFormSubmit.bind(this));
    this.sortBy?.addEventListener('change', this.onSelectChange.bind(this));
    this.viewMoreButton?.addEventListener('click', this.onViewMoreClick.bind(this));

    this.boundResizeHandler = () => {
      if (this.resizeRaf) {
        window.cancelAnimationFrame(this.resizeRaf);
      }
      this.resizeRaf = window.requestAnimationFrame(() => {
        this._syncTouchCardCtasInGrid();
      });
    };
    window.addEventListener('resize', this.boundResizeHandler);
  }

  /**
   * Clean up event listeners when element is removed
   */
  disconnectedCallback() {
    // Clean up abort controller
    if (this.abortController) {
      this.abortController.abort();
    }

    // Remove event listeners from facet elements
    this.facetsFormContainer?.querySelectorAll('input').forEach((item) => {
      item.removeEventListener('change', this.boundFacetChangeHandler);
    });

    this.facetsFormContainer?.querySelectorAll('.facet-clear--single').forEach((item) => {
      item.removeEventListener('click', this.boundFacetClearHandler);
    });

    // Clean up event listeners from main elements
    this.searchForm?.removeEventListener('submit', this.onFormSubmit);
    this.sortBy?.removeEventListener('change', this.onSelectChange);
    this.viewMoreButton?.removeEventListener('click', this.onViewMoreClick);
    if (this.boundResizeHandler) {
      window.removeEventListener('resize', this.boundResizeHandler);
    }
    if (this.resizeRaf) {
      window.cancelAnimationFrame(this.resizeRaf);
      this.resizeRaf = null;
    }
  }

  onFormSubmit(event) {
    event.preventDefault();
    this.clearFilters();
    this.setLoadingState();
    this.getNostoSearchResults();
    this.getSearchResults();
  }

  onFormReset(event) {
    event.preventDefault();

    this.input.value = '';
    this.input.focus();
    this.toggleResetButton();
    this.searchTerm = '';
    this.abortController.abort();
    this.abortController = new AbortController();
    this.closeResults(true);
  }

  onSelectChange(event) {
    if (this.usingFallback) {
      // Let Shopify sorting handle it
      return;
    }
    const value = event.target.value;
    const params = new URLSearchParams(window.location.search);
    params.set('sort_by', value);
    this.updateQuery(params);
    this.setLoadingState();
    this.getNostoSearchResults();
    this.getSearchResults();
  }

  onViewMoreClick(event) {
    event.preventDefault();
    const params = new URLSearchParams(window.location.search);

    // Update the current page
    this.currentPage++;
    params.set('page', this.currentPage);

    // Remove legacy 'from' parameter if it exists
    params.delete('from');

    this.updateQuery(params);
    this.setLoadingState();
    this.getNostoSearchResults();
    this.getSearchResults();
  }

  updateQuery(params = null) {
    params = params ? params : new URLSearchParams(window.location.search);
    if (this.context == 'serp') {
      const searchTerm = this.getQuery();
      if (!searchTerm.length) return;
      this.searchTerm = searchTerm;
      params.set('q', searchTerm);
    }
    const queryStr = params.toString();
    const url = !queryStr ? window.location.pathname : `${window.location.pathname}?${queryStr}`;
    window.history.replaceState({ path: url }, '', url);
  }

  clearFilters() {
    const params = new URLSearchParams(window.location.search);
    this.activeMaxPrice = null;
    for (const [param, value] of params.entries()) {
      if (param.startsWith('filter.')) {
        params.delete(param);
      }
    }
    // Reset to page 1 when clearing filters
    params.delete('page');
    this.currentPage = 1;
    this.updateQuery(params);
  }

  getQuery() {
    return this.input.value.trim();
  }

  /**
   * Get search results from cache, Nosto API or fallback to Shopify
   */
  async getSearchResults() {
    // Update header text for search context
    if (this.context === 'serp' && this.header) {
      this.header.textContent = `${window.nostoStrings?.searchResultsFor || 'Search results for'} "${this.searchTerm}"`;
    }
    const cacheKey = location.search || location.href;

    // Try to get cached results first for better performance
    if (!this.usingFallback && this.cachedResults[cacheKey]) {
      this.renderSearchResults(this.cachedResults[cacheKey]);
      return;
    }

    // Check if we should skip Nosto due to previous failures
    this.checkNostoAvailability();

    if (this.usingFallback && this.enableFallback) {
      return this.fallbackToShopify();
    }

    try {
      // Create a new abort controller for this request
      this.abortController = new AbortController();

      // Get Nosto search results
      // const nostoResults = await this.results;
      const nostoResults = await window.preloadedNostoResults;

      if (nostoResults?.products?.hits) {
        // Cache the results for faster subsequent requests
        this.cachedResults[cacheKey] = nostoResults;
        this.renderSearchResults(nostoResults);

        // Reset retry counter on success
        sessionStorage.removeItem('fallbackAttempts');
      } else {
        // Handle empty results
        this.renderNoResults();

        // Reset retry counter as this is a valid response (just empty)
        sessionStorage.removeItem('fallbackAttempts');
      }
    } catch (error) {
      console.error('Nosto search error:', error);

      // Get current attempt count from sessionStorage
      const currentAttempts = parseInt(sessionStorage.getItem('fallbackAttempts') || '0');
      const newAttempts = currentAttempts + 1;

      // Store updated attempt count
      sessionStorage.setItem('fallbackAttempts', String(newAttempts));

      console.log(`Nosto API attempt ${newAttempts} of ${this.maxRetries} failed`);

      if (newAttempts >= this.maxRetries) {
        console.log('Max attempts reached, setting skipNostoUntil and falling back to Shopify');

        if (this.enableFallback) {
          // Set skipNostoUntil in sessionStorage for 10 minutes
          sessionStorage.setItem('skipNostoUntil', String(Date.now() + 600000));
          return this.fallbackToShopify();
        } else {
          // Show error state if fallback is disabled
          this.removeLoadingState();
          this.renderErrorState();
        }
      } else {
        // Immediately fallback to Shopify without retrying Nosto
        if (this.enableFallback) {
          return this.fallbackToShopify();
        } else {
          // Show error state if fallback is disabled
          this.removeLoadingState();
          this.renderErrorState();
        }
      }
      return; // Exit early to avoid updating pagination state
    }

    // Update pagination state after results are loaded
    this.updatePaginationState();
  }

  /**
   * Check if Nosto should be skipped based on previous failures
   */
  checkNostoAvailability() {
    const skipNostoUntil = sessionStorage.getItem('skipNostoUntil');
    const currentTime = Date.now();

    if (skipNostoUntil && currentTime <= parseInt(skipNostoUntil)) {
      this.usingFallback = true;
      // Reset retry counter when using fallback
      this.retryCount = 0;
    } else {
      this.usingFallback = false;
      // Clear expired setting
      if (skipNostoUntil) {
        sessionStorage.removeItem('skipNostoUntil');
      }
    }
  }

  async fallbackToShopify() {
    this.usingFallback = true;

    try {
      // Instead of fetching HTML, redirect to the same URL with view=fallback parameter
      const currentParams = new URLSearchParams(window.location.search);

      // Add the view parameter to trigger the fallback template
      currentParams.set('view', 'fallback');

      // If we're on search page and have a search term, make sure it's in the params
      if (this.context === 'serp' && this.searchTerm && !currentParams.has('q')) {
        currentParams.set('q', this.searchTerm);
      }

      // Construct the fallback URL
      const fallbackUrl = `${window.location.pathname}?${currentParams.toString()}`;

      // Redirect to the fallback URL
      window.location.href = fallbackUrl;
    } catch (err) {
      console.error('Shopify fallback error:', err);
      // In case of an error with redirection, show an error message
      this.renderErrorState();
    }
  }

  async getNostoSearchResults() {
    const params = new URLSearchParams(window.location.search);
    const isKeyword = params.get('keyword') == 'true' ? true : false;
    const filters = [];
    const sortBy = [];

    if (params.has('sort_by')) {
      const sortValue = params.get('sort_by');
      // Skip relevance, since it's the default sort
      if (sortValue !== 'relevance') {
        let field = sortValue;
        let order = 'asc';
        if (sortValue.endsWith('-ascending')) {
          field = sortValue.replace('-ascending', '');
          order = 'asc';
        } else if (sortValue.endsWith('-descending')) {
          field = sortValue.replace('-descending', '');
          order = 'desc';
        }
        if (!field.includes('price')) {
          field = `stats.${field}`;
        }
        sortBy.push({
          field: field,
          order: order
        });
      }
    }

    // Calculate 'from' based on current page
    const currentPage = parseInt(params.get('page')) || 1;
    const from = (currentPage - 1) * this.resultsSize;

    // Iterate through all params to build filters array
    for (const [param, value] of params.entries()) {
      // Skip non-filter params
      if (!param.startsWith('filter.') || filters.some(f => f.field === 'price')) continue;
      if (param === 'filter.price') {
        const [range, max] = value.split('~');
        const [gte, lte] = range.split('-');
        filters.push({
          field: 'price',
          range: { lte: lte, gte: gte }
        });
      } else {
        // Remove filter. prefix to get param name
        const paramName = param.replace('filter.', '').toLowerCase();
        // Get the corresponding API field from window.filterToNosto
        const apiField = window.filterToNosto && window.filterToNosto[paramName] ? window.filterToNosto[paramName] : paramName;
        if (!apiField) {
          console.error(`Invalid filter parameter: ${paramName}`);
          continue;
        };
        // Split value string into array by pipe character
        const valueArray = value.split('|');
        filters.push({
          field: apiField,
          value: valueArray
        });
      }
    }
    // Parameters for the Nosto. Remember to keep these in sync with nosto-async.liquid (initial load of the page)
    try {
      const searchParams = {
        products: {
          facets: ['*'],
          fields: [
            'name',
            'imageUrl',
            'alternateImageUrls',
            'price',
            'url',
            'brand',
            'listPrice',
            'customFields.key',
            'customFields.value',
            'categoryIds',
            'skus.id',
            'skus.name',
            'skus.price',
            'skus.listPrice',
            'skus.availability'
          ],
          size: this.resultsSize,
          from: from // Use calculated 'from' value
        }
      };

      const trackParams = {
        redirect: true,
        track: this.context,
        isKeyword: isKeyword
      }

      if (this.context === 'serp') {
        searchParams.query = this.searchTerm;
      } else if (this.context === 'category') {
        if (location.pathname !== '/collections/all') {
          searchParams.products.categoryId = this.collectionId;
        }
      }

      if (filters.length > 0) {
        searchParams.products.filter = filters;
      }

      if (sortBy.length > 0) {
        searchParams.products.sort = sortBy;
      }

      window.preloadedNostoResults = new Promise(resolve => {
        nostojs(async api => {
          if (this.context === 'serp') {
            api.recordSearchSubmit(searchParams.query)
          }
          const results = api.search(searchParams, trackParams)

          if (!results || results.status) {
            throw new Error('Invalid Nosto API response');
          }
          resolve(results);
        });
      });

    } catch (error) {
      console.log(error)
      throw error;
    }
  }

  async renderSearchResults(results) {
    this.classList.remove('no-results')
    const products = results.products.hits;
    const resultsFrom = results.products.from;
    const resultsTotal = results.products.total;
    if (resultsTotal <= resultsFrom + products.length) {
      this.viewMoreButton.style.display = 'none';
    } else {
      this.viewMoreButton.style.display = 'block';
    }

    if (!products || products.length === 0) {
      this.renderNoResults();
      return;
    }
    this.processExcludeFacets(products[0]);
    if (this.context === 'serp') {
      document.title = `Search: ${resultsTotal} results found for "${this.searchTerm}"`;
    }
    this.resultsCount.textContent = `${resultsTotal} ${window.nostoStrings.searchResults}`;
    if (this.showCount) {
      const showFrom = resultsFrom + products.length;
      const showCount = showFrom > resultsTotal ? resultsTotal : showFrom;
      const countText = window.nostoStrings.searchShowCount || 'Showing {{ count }} of {{ total }} items';
      this.showCount.textContent = countText
        .replace('{{ count }}', showCount)
        .replace('{{ total }}', resultsTotal);
    }
    this.productGrid.setAttribute('data-filtered-count', results.products.total);
    this.renderFacets(results.products.facets);
    const productsMarkup = await this.generateProductsMarkup(products);

    if (resultsFrom > 0) {
      // For pagination/load more - append without destroying existing nodes/listeners
      this.productGrid.insertAdjacentHTML('beforeend', productsMarkup);
    } else {
      // For initial load or filter change - replace content
      this.productGrid.innerHTML = productsMarkup;
    }

    // Check if content slots are available in options
    const contentSlots = this.options.contentSlots;
    if (contentSlots && Array.isArray(contentSlots) && contentSlots.length > 0) {
      // Sort content slots by position to insert them in order
      const sortedSlots = [...contentSlots].sort((a, b) => (a.position || 0) - (b.position || 0));

      // Insert each content slot at its specified position
      sortedSlots.forEach(contentSlot => {
        const contentSlotKey = contentSlot.key || `${contentSlot.position || ''}|${contentSlot.link || ''}|${contentSlot.image || ''}`;
        if (this.productGrid.querySelector(`.card--content-slot[data-content-slot-key="${contentSlotKey}"]`)) {
          return;
        }
        const contentSlotElem = this._generateContentSlotElement(contentSlot);
        if (!contentSlotElem) {
          return;
        }
        const refNode = this.productGrid.querySelector(`.card--product[data-index="${contentSlot.position}"]`) ?? this.productGrid.lastChild;
        this.productGrid.insertBefore(contentSlotElem, refNode);
      });
    }

    // Add event listeners for image loading
    this.setupImageLoadingEvents();

    // Load swatches after products are rendered (non-blocking)
    this.loadDeferredSwatches();

    // Set up image carousels after products are in the DOM
    this._setupAllImageCarousels();

    // Ensure CTA placement matches current viewport mode (touch/mobile vs desktop)
    this._syncTouchCardCtasInGrid();

    // Replace nr of results in document title
    this.removeLoadingState();

    // On initial page load with ?page=N > 1, scroll to the start of that page's products
    if (!this.initialLoadComplete && this.currentPage > 1) {
      const scrollTargetIndex = (this.currentPage - 1) * this.resultsSize + 1;
      const targetCard = this.productGrid.querySelector(`.card--product[data-index="${scrollTargetIndex}"]`);
      if (targetCard) {
        requestAnimationFrame(() => {
          targetCard.scrollIntoView({ behavior: 'instant', block: 'start' });
        });
      }
    }
    this.initialLoadComplete = true;

    // Dispatch custom event after Nosto products are rendered
    document.dispatchEvent(new CustomEvent('nosto:rendered', { detail: { placement: this } }));
  }

  processExcludeFacets(product) {
    const customFields = product.customFields;
    if (this.includeFacetRules && this.context === 'category') {
      let division;
      if (Array.isArray(customFields)) {
        division = customFields.find(f => f.key === 'd365-division')?.value?.toLowerCase();
      }
      if (division && !this.includeFacetRules[division]) {
        // Push all facets to excludeFacets where the division key doesn't match
        Object.keys(this.includeFacetRules).forEach(key => {
          if (key !== division) {
            this.excludeFacets.push(this.includeFacetRules[key]);
          }
        });
      }
    }
  }

  // New method to handle image loading events
  setupImageLoadingEvents() {
    const images = this.productGrid.querySelectorAll('img:not(.loaded)');
    images.forEach(img => {
      if (img.complete) {
        img.classList.add('loaded');
      } else {
        img.addEventListener('load', () => {
          img.classList.add('loaded');
        });
      }
    });
  }

  /**
   * Display an error state when both Nosto and fallback fail
   */
  renderErrorState() {
    if (!this.productGrid) return;

    this.productGrid.innerHTML = `
          <div class="mt-gutter mb-gutter">
            <p role="status">There was an error retrieving search results. Please try again later.</p>
            <a class="link" href="/">${window.nostoStrings?.continueShopping || 'Continue shopping'}</a>
          </div>
        `;
  }

  async renderFacets(facets) {
    if (!this.facetsFormContainer || !facets || !Array.isArray(facets)) {
      console.warn('Cannot render facets: Missing required elements or data');
      return;
    }

    // Store open facets state before clearing
    this.saveOpenFacetsState();

    // Get template references
    const templates = this.getFacetTemplates();
    if (!templates.facetsDetails) {
      console.error('Required facet templates not found');
      return;
    }

    // Clear existing facets
    this.facetsFormContainer.innerHTML = '';

    // Process each facet
    facets.forEach(facet => this.renderSingleFacet(facet, templates));

    // Add event listeners to new facet elements
    this.attachFacetEventListeners();

    // Scroll to last edited facet if applicable
    this.scrollToLastEditedFacet();
  }

  // Helper functions for facet rendering
  saveOpenFacetsState() {
    const openDetails = this.facetsFormContainer?.querySelectorAll('details[open]') || [];
    this.openedFacets = Array.from(openDetails).map(detail =>
      detail.getAttribute('data-filter-group')
    ).filter(Boolean);
  }

  getFacetTemplates() {
    return {
      facetsDetails: document.querySelector('#nosto-facets-details-template'),
      facetsListItem: document.querySelector('#nosto-facets-list-item-template'),
      facetsColor: document.querySelector('#nosto-facets-color-swatches-template'),
      facetsRange: document.querySelector('#nosto-facets-range-template')
    };
  }

  renderSingleFacet(facet, templates) {
    if (!facet || !facet.name) return;
    if (this.excludeFacets.includes(facet.name.toLowerCase())) return;

    const content = templates.facetsDetails.content.cloneNode(true);
    const tempContainer = document.createElement('div');
    tempContainer.appendChild(content);

    const tempContainerContentElem = tempContainer.querySelector('[data-facets-container]');
    const filterLabel = tempContainer.querySelector('summary span');
    filterLabel.textContent = facet.name.charAt(0).toUpperCase() + facet.name.slice(1).toLowerCase();

    // Set up details element
    const detailsElem = tempContainer.querySelector('details');
    detailsElem.setAttribute('data-filter-group', facet.name);

    // Restore open state if this facet was open before
    if (this.openedFacets.includes(facet.name)) {
      detailsElem.setAttribute('open', '');
    }

    let facetActive = false;

    // Handle different facet types
    if (facet.type === 'terms') {
      facetActive = this.renderTermsFacet(facet, tempContainerContentElem, templates);
    } else if (facet.type === 'stats' && facet.name === "Price") {
      facetActive = this.renderPriceFacet(facet, tempContainerContentElem, templates);
    }

    // Toggle clear button visibility
    const clearButton = tempContainer.querySelector('.facet-clear');
    if (clearButton) {
      clearButton.style.display = facetActive ? 'block' : 'none';
    }

    // Add to the DOM
    this.facetsFormContainer.appendChild(tempContainer.firstElementChild);
  }

  renderTermsFacet(facet, containerElem, templates) {
    let facetActive = false;

    // Determine filter type
    let filterType = 'default';
    if (facet.name === 'Colour') {
      filterType = 'color';
    }

    // Create list container
    const listContainer = document.createElement('ul');
    listContainer.classList.add('filter-options-list', filterType);
    containerElem.appendChild(listContainer);

    // Add each facet value
    facet.data.forEach(item => {
      const content = templates.facetsListItem.content.cloneNode(true);
      const listItemContainer = document.createElement('li');
      listItemContainer.classList.add('filter-option');
      listItemContainer.appendChild(content);

      // Get parameter name, using mapping if available
      const facetNameLower = facet.name.toLowerCase().replace(' ', '-');
      const paramName = window.nostoToFilter && window.nostoToFilter[facet.field]
        ? window.nostoToFilter[facet.field]
        : facetNameLower;

      if (!window.nostoToFilter[facet.field]) {
        window.filterToNosto[facetNameLower] = facet.field;
      }

      // Replace placeholders in template
      listItemContainer.innerHTML = listItemContainer.innerHTML
        .replace(/\[\[param_name\]\]/g, paramName)
        .replace(/\[\[value\]\]/g, item.value)
        .replace(/\[\[count\]\]/g, item.count)
        .replace(/\[\[filter_type\]\]/g, filterType);

      const input = listItemContainer.querySelector('input');
      const inputClassType = filterType === 'color' ? 'for-swatch' : 'checkbox';
      input.classList.add(inputClassType);

      // Handle color swatches
      if (filterType === 'color') {
        this.addColorSwatch(listItemContainer, item.value, templates);
      }

      // Set checked state
      if (item.selected) {
        input.checked = true;
        facetActive = true;
      } else {
        input.removeAttribute('checked');
      }

      listContainer.appendChild(listItemContainer);
    });

    // Add search input for facets that should be searchable
    if (this.searchableFacets.includes(facet.field.toLowerCase())) {
      this.renderFacetSearch(listContainer, facet.field);
    }

    return facetActive;
  }

  addColorSwatch(container, colorValue, templates) {
    const content = templates.facetsColor.content.cloneNode(true);
    const swatchContainer = document.createElement('span');
    swatchContainer.classList.add('swatch');
    swatchContainer.appendChild(content);

    // Get color value, using mapping if available
    let facetColor = colorValue.toLowerCase();
    if (window.colorPatterns && window.colorPatterns[facetColor]) {
      facetColor = window.colorPatterns[facetColor];
    }

    swatchContainer.innerHTML = swatchContainer.innerHTML
      .replace(/\[\[color\]\]/g, colorValue)
      .replace(/\[\[color_hex\]\]/g, facetColor);

    const spanLabel = container.querySelector('span[data-option-label]');
    container.querySelector('label').insertBefore(swatchContainer, spanLabel);
  }

  renderPriceFacet(facet, containerElem, templates) {
    const content = templates.facetsRange.content.cloneNode(true);
    const priceRangeTempContainer = document.createElement('div');
    priceRangeTempContainer.appendChild(content);

    const params = new URLSearchParams(window.location.search);
    const priceParam = params.get('filter.price');
    let newMinValue = 0;
    let newMaxValue = facet.max;
    let maxPrice = facet.max;
    let facetActive = false;

    if (priceParam) {
      const [range, max] = priceParam.split('~');
      const [gte, lte] = range.split('-');
      newMinValue = parseInt(gte) || 0;
      newMaxValue = parseInt(lte) || facet.max;
      maxPrice = parseInt(max) || facet.max;
      facetActive = true;
    } else {
      this.activeMaxPrice = facet.max;
    }

    priceRangeTempContainer.innerHTML = priceRangeTempContainer.innerHTML
      .replace(/\[\[min_value\]\]/g, newMinValue)
      .replace(/\[\[max_value\]\]/g, newMaxValue)
      .replace(/\[\[gte_max\]\]/g, maxPrice - 1)
      .replace(/\[\[lte_max\]\]/g, maxPrice)
      .replace(/\[\[lte_min\]\]/g, newMinValue + 1);

    containerElem.appendChild(priceRangeTempContainer);

    return facetActive;
  }

  attachFacetEventListeners() {
    // Add change event listeners to inputs
    this.facetsFormContainer.querySelectorAll('input').forEach((item) => {
      item.addEventListener('change', debounce(this.boundFacetChangeHandler, 150));
    });

    // Add click event listeners to clear buttons
    this.facetsFormContainer.querySelectorAll('.facet-clear').forEach((item) => {
      item.addEventListener('click', debounce(this.boundFacetClearHandler, 150));
    });

    // Initialize accordions for details elements
    this.facetsFormContainer.querySelectorAll('details').forEach((el) => {
      new Accordion(el);
    });
  }

  scrollToLastEditedFacet() {
    if (!this.lastEditedFacet) return;

    const lastEditedFacetInput = this.facetsFormContainer.querySelector(
      `.filter-option input[data-key-value="${this.lastEditedFacet}"]`
    );

    if (lastEditedFacetInput) {
      // Use smooth scrolling and add slight delay to ensure the facet is open
      setTimeout(() => {
        lastEditedFacetInput.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }, 100);
    }
  }

  renderFacetSearch(container, facetTitle) {
    const searchLabel = document.createElement('label');
    const inputTextContent = `${window.nostoStrings.search} ${facetTitle}s`;
    searchLabel.textContent = inputTextContent;
    searchLabel.classList.add('visually-hidden'); // Hides label visually but keeps it accessible

    const searchInput = document.createElement('input');
    searchInput.type = 'text';
    searchInput.placeholder = inputTextContent;
    searchInput.classList.add('facet-search-field');
    searchInput.setAttribute('aria-label', inputTextContent);

    const searchWrapper = document.createElement('div');
    searchWrapper.classList.add('facet-search');
    searchWrapper.appendChild(searchLabel);
    searchWrapper.appendChild(searchInput);

    container.parentNode.insertBefore(searchWrapper, container);

    searchInput.addEventListener('input', (e) => {
      const searchValue = e.target.value.toLowerCase();
      const listItems = container.querySelectorAll('li');
      listItems.forEach(item => {
        const label = item.querySelector('[data-option-label]').textContent.toLowerCase();
        item.style.display = label.includes(searchValue) ? '' : 'none';
      });
    });
  }

  async onFacetChange(event) {
    if (this.usingFallback) {
      // Let Shopify facets handle it
      return;
    }
    const target = event.target;
    if (target.classList.contains('facet-search-field')) return;
    const params = new URLSearchParams(window.location.search);
    const isPriceRange = target.classList.contains('price-range__input');
    const paramTitle = target.getAttribute('data-param-name').toLowerCase();

    // Reset pagination when changing filters
    params.delete('page');
    this.currentPage = 1;

    if (isPriceRange) {
      // Every price facet change triggers events for both min and max inputs, skip the min input and handle only the event from max input
      // If selected value is out of bounds, remove filter value from parameters
      const inputMin = target.getAttribute('name') === 'filter.price.gte';
      const inputMax = target.getAttribute('name') === 'filter.price.lte';
      if (inputMin && target.value < Math.floor(target.min)) {
        target.value = target.min;
        return;
      } else if (inputMax && target.value > Math.floor(target.max)) {
        target.value = target.max;
        return;
      } else {
        const minInput = this.facetsFormContainer.querySelector('input[name="filter.price.gte"]');
        const maxInput = this.facetsFormContainer.querySelector('input[name="filter.price.lte"]');

        // Skip if values are out of bounds
        if (parseFloat(minInput.value) < parseFloat(minInput.min) ||
          parseFloat(maxInput.value) > parseFloat(maxInput.max)) {
          return;
        }

        // Construct the new price parameter
        const priceValue = `${minInput.value}-${maxInput.value}`;
        if (this.activeMaxPrice) {
          params.set('filter.price', `${priceValue}~${this.activeMaxPrice}`);
        } else {
          params.set('filter.price', priceValue);
        }
      }
    }

    const keyValue = target.getAttribute('data-key-value');
    const param = `filter.${paramTitle}`;
    const value = target.getAttribute('data-value') || target.value;

    if (!param || !value) return;

    const existingValues = params.get(param)?.split('|') || [];
    if (!isPriceRange) {
      if (target.checked) {
        existingValues.push(value);
        params.set(param, existingValues.join('|'));
        this.lastEditedFacet = keyValue;
      } else {
        const newValues = existingValues.filter(v => v !== value);
        if (newValues.length === 0) {
          params.delete(param);
          this.lastEditedFacet = null;
        } else {
          params.set(param, newValues.join('|'));
        }
      }
    }

    this.updateQuery(params);
    // Skip event from min price input, price change is handled from max input
    if (target.getAttribute('data-min') !== null) return;
    this.setLoadingState();
    this.getNostoSearchResults();
    this.getSearchResults();
  }

  async onFacetClear(event) {
    event.preventDefault();
    const target = event.target;
    const params = new URLSearchParams(window.location.search);
    const parent = target.closest('details');
    const field = parent.getAttribute('data-filter-group')?.toLowerCase();

    // Reset pagination when clearing filters
    params.delete('page');
    this.currentPage = 1;

    if (field == 'price') {
      params.delete('filter.price');
      this.activeMaxPrice = null;
    } else {
      const inputs = parent.querySelectorAll('input');
      inputs.forEach((input) => {
        input.checked = false;
      });

      params.delete(`filter.${field}`);
      this.lastEditedFacet = null;
    }

    this.updateQuery(params);
    this.setLoadingState();
    this.getNostoSearchResults();
    this.getSearchResults();
  }

  renderNoResults() {
    if (!this.productGrid) return;

    const noResultsMessage = window.nostoStrings?.searchNoResults || 'No results for "{{terms}}"';
    const continueShoppingText = window.nostoStrings?.continueShopping || 'Continue shopping';
    this.classList.add('no-results');

    this.productGrid.innerHTML = `
          <div class="mt-gutter mb-gutter text-container">
            <p role="status">${noResultsMessage.replace('{{ terms }}', this.searchTerm || '')}</p>
            <a class="link" href="/">${continueShoppingText}</a>
          </div>
        `;
    this.removeLoadingState();

    // Update page title
    if (this.context === 'serp') {
      document.title = `Search: No results for "${this.searchTerm}"`;
    }
  }
  sanitizeProductUrl(url) {
    if (!url || typeof url !== 'string') return '';
    const trimmed = url.trim();
    if (!trimmed) return '';

    try {
      const parsed = new URL(trimmed, window.location.origin);
      if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return '';
      return parsed.href;
    } catch (e) {
      return '';
    }
  }
}

if (!customElements.get('nosto-search')) {
  customElements.define('nosto-search', NostoSearch);
}
